import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import ConfigBeneficioModal from '../components/ConfigBeneficioModal';
import DiferencaMesAnterior from '../components/DiferencaMesAnterior';
import TravaBeneficio from '../components/TravaBeneficio';
import '../styles/bporh_admissoes.css';

// Controle de Benefícios (vale-alimentação) — a "planilha viva" do operador:
// todos os colaboradores do espelho Convenia com vínculo, custo/dia (padrão do
// vínculo ou valor individual editável), dias úteis a pagar no mês (proporcional
// a admissão/desligamento, descontando férias/afastamento) e alertas.
// Modelo: planilha da Órulo (CPF, nome, vínculo, admissão, departamento,
// custo dia, dias, desc, total, crédito, obs).

const brl = (v) => (v == null ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

// O mês da tela é o do PAGAMENTO: o VA pago em setembro é o crédito que o
// colaborador usa em outubro, e o desconto sai na folha de setembro.
const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// mês seguinte a um 'AAAA-MM' (o mês em que o crédito é usado)
const mesDepois = (m) => {
  const [ano, mm] = (m || '').split('-').map(Number);
  if (!ano || !mm) return m || '';
  const d = new Date(ano, mm, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// 'AAAA-MM' -> 'setembro/2026'
const mesExtenso = (m) => {
  const [ano, mm] = (m || '').split('-');
  if (!ano || !mm) return m || '';
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${nomes[Number(mm) - 1] || mm}/${ano}`;
};

const COR_ALERTA = {
  ferias: { bg: '#e3f2fd', cor: '#0a6ea1', borda: '#0a6ea1', icone: 'fa-umbrella-beach' },
  afastamento: { bg: '#f3e8fb', cor: '#7a3b9e', borda: '#7a3b9e', icone: 'fa-notes-medical' },
  novo: { bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971', icone: 'fa-user-plus' },
  desligado: { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-user-xmark' },
  experiencia: { bg: '#fff8e6', cor: '#8a6d00', borda: '#f1be48', icone: 'fa-hourglass-half' },
};

// A MESMA tela serve dois benefícios. A mecânica é idêntica — valor por
// vínculo ou individual, dias descontados por férias e afastamento, feriados,
// data do crédito, boleto, exportação. O que muda é o rótulo, a base de dias e
// o layout do arquivo, e disso quem cuida é o backend pelo ?tipo=.
// pessoas do mês para o "por que deu diferença" (quem recebe = crédito > 0);
// os que não recebem entram com valor 0 e o motivo, para explicar quem saiu
const pessoasDoMes = (d) => [
  ...(d?.colaboradores || []).map(c => ({
    id: c.convenia_id, nome: c.nome, valor: c.credito || 0, dias: c.dias_pagar,
    admissao: c.admissao, desligado: c.desligado,
    motivo: (c.alertas || []).map(a => a.texto).filter(Boolean)[0] || null,
  })),
  ...(d?.excluidos || []).map(x => ({
    id: x.convenia_id, nome: x.nome, valor: 0, motivo: x.motivo || 'não recebe',
  })),
];

const ROTULOS = {
  va: { titulo: 'Controle de VA', icone: 'fa-utensils', curto: 'VA' },
  home_office: { titulo: 'Auxílio Home Office', icone: 'fa-house-laptop',
                 curto: 'home office' },
};

// vínculos como a Convenia devolve (relationship.name): o padrão casa pelo nome
const VINCULOS_CONVENIA = ['CLT', 'Estágio', 'Aprendiz', 'Contrato por Tempo Determinado',
  'Pessoa Jurídica', 'Trabalhador Autônomo', 'Sócio'];

const BpoRhBeneficios = ({ tipo = 'va' }) => {
  // acessível de dois jeitos: menu "Controle de Benefícios" (sem cliente na
  // rota — escolhe a empresa no seletor) ou link direto /cliente/:id
  const { clienteId: clienteIdRota } = useParams();
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const rotulo = ROTULOS[tipo] || ROTULOS.va;
  // O ?tipo= vai na URL de TODA chamada (e não em `params`) porque alguns
  // envios são multipart ou já usam 'tipo' no corpo para outra coisa.
  const q = `?tipo=${tipo}`;

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // empresa e mês vivem na URL (?cliente=&mes=): o F5 mantém a tela aberta em
  // vez de voltar para a listagem, e o link fica compartilhável
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteSel = searchParams.get('cliente') || '';
  // entrar numa empresa EMPILHA no histórico (replace:false): o voltar do
  // navegador devolve para a lista de portais, e não para a página anterior
  const setClienteSel = (v) => setSearchParams(p => {
    const n = new URLSearchParams(p);
    if (v) n.set('cliente', String(v)); else n.delete('cliente');
    return n;
  }, { replace: false });
  const clienteId = clienteIdRota || clienteSel;
  const [clientes, setClientes] = useState([]);
  const [portaisGeral, setPortaisGeral] = useState([]);
  useEffect(() => {
    if (clienteIdRota) return;
    // todas as empresas: quem não tem Convenia (ex.: WNS) ainda mostra o
    // portal do VA — só a lista de colaboradores fica vazia
    axios.get(`${API}/api/bporh-clientes/`, { headers: headers() })
      .then((res) => setClientes(res.data.clientes || []))
      .catch(() => setMensagem('❌ Não foi possível carregar as empresas'));
    // entrada da página: os VAs de todas as empresas (formato /BpoRh/Benefícios)
    axios.get(`${API}/api/bpo-beneficios/portais/${q}`, { headers: headers() })
      .then((res) => setPortaisGeral(res.data.servicos || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // `mes` é o mês de COMPRA/pagamento (o backend devolve mes_uso = mês seguinte,
  // em que o colaborador usa o crédito). O Painel do Operador nomeia a
  // competência pelo mês de USO ("VA 10/2026 até 25/09"): mesma coisa, outro rótulo.
  const mes = searchParams.get('mes') || mesAtual();
  const setMes = (v) => setSearchParams(p => {
    const n = new URLSearchParams(p);
    if (v) n.set('mes', v); else n.delete('mes');
    return n;
  }, { replace: true });
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroVinculo, setFiltroVinculo] = useState('');
  // filtro por coluna, estilo planilha (21/10/2026)
  const FCOL_VAZIO = { pago: '', cpf: '', nome: '', filial: '', admissao: '', depto: '', credito: '', obs: '',
    v_custo_dia: '', v_custo_mes: '', v_dias: '', v_desconto: '', v_total: '', v_credito: '' };
  const [fcol, setFcol] = useState(FCOL_VAZIO);
  const mudarFcol = (campo, valor) => setFcol((f) => ({ ...f, [campo]: valor }));
  const temFcol = Object.values(fcol).some(Boolean);

  const carregar = useCallback((silencioso = false) => {
    if (!clienteId) { setDados(null); return; }
    if (!silencioso) setCarregando(true);
    axios.get(`${API}/api/bpo-beneficios/${clienteId}/${q}`, { params: { mes }, headers: headers() })
      .then((res) => setDados(res.data))
      .catch((error) => {
        setDados(null);
        setMensagem(`❌ ${error.response?.data?.erro || 'Não foi possível carregar os benefícios'}`);
      })
      .finally(() => setCarregando(false));
  }, [clienteId, mes]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [carregar]);
  // e-mail enviado = mês travado: a tela mostra a foto e o que é do mês fica bloqueado
  const travado = !!dados?.trava?.travado;

  // edição (custo individual / obs / padrão por vínculo) — salva no blur
  const [configAberto, setConfigAberto] = useState(false);   // botão Configurar (dias + valor mensal)
  // vínculos incluídos à mão no "Padrão por vínculo" (ainda sem colaborador ou
  // sem valor salvo). Ficam gravados quando a operadora digita o valor.
  const [vinculosExtra, setVinculosExtra] = useState([]);
  const [novoVinculo, setNovoVinculo] = useState(null);      // null = fechado; '' = escolhendo
  const salvar = (corpo, okMsg) => axios
    .patch(`${API}/api/bpo-beneficios/${clienteId}/editar/${q}`, corpo, { headers: headers() })
    .then(() => { if (okMsg) setMensagem(`✅ ${okMsg}`); carregar(true); })
    .catch((error) => setMensagem(`❌ ${error.response?.data?.mensagem || 'Não foi possível salvar'}`));

  const salvarCusto = (c, valor) => {
    const v = valor.trim().replace(',', '.');
    const atual = c.custo_individual ? String(c.custo_dia) : '';
    if (v === atual || (v === '' && !c.custo_individual)) return;   // nada mudou
    salvar({ tipo: 'colaborador', convenia_id: c.convenia_id, custo_dia: v === '' ? null : v },
      v === '' ? `${c.nome}: voltou ao padrão do vínculo` : `${c.nome}: custo/dia salvo`);
  };
  // valor FIXO mensal do colaborador (custo_mes): com ele o crédito é o mensal
  // rateado pelos dias pagos; vazio volta ao custo/dia
  const salvarFixo = (c, valor) => {
    const v = valor.trim().replace(',', '.');
    const atual = c.custo_mes != null ? String(c.custo_mes) : '';
    if (v === atual) return;
    salvar({ tipo: 'colaborador', convenia_id: c.convenia_id, custo_mes: v === '' ? null : v },
      v === '' ? `${c.nome}: valor fixo removido` : `${c.nome}: valor fixo salvo`);
  };
  const salvarObs = (c, valor) => {
    if ((valor || '').trim() === (c.obs || '')) return;
    salvar({ tipo: 'colaborador', convenia_id: c.convenia_id, obs: valor }, null);
  };
  const salvarPadraoFixo = (vinculo, valor) => {
    const v = valor.trim().replace(',', '.');
    salvar({ tipo: 'padrao', vinculo, custo_mes: v === '' ? null : v },
      v === '' ? `${vinculo}: valor fixo removido` : `${vinculo}: valor fixo salvo`);
  };
  // custo por FILIAL (Gauss: SP R$ 30,00 · MG R$ 31,43) — vale entre o individual e o vínculo
  const salvarFilialValor = (filial, campo, valor) => {
    const v = valor.trim().replace(',', '.');
    const atual = dados?.padroes_filial?.[String(filial.id)]?.[campo];
    if ((v === '' && atual == null) || (v !== '' && Number(v) === Number(atual))) return;
    salvar({ tipo: 'filial', filial_id: filial.id, [campo]: v === '' ? null : v },
      `${filial.nome}: ${campo === 'custo_mes' ? 'valor fixo' : 'custo/dia'} ${v === '' ? 'removido' : 'salvo'}`);
  };
  // filial do colaborador (a mesma da folha): muda o custo que vale para ele
  const salvarFilialColab = (c, filialId) => {
    if (String(filialId || '') === String(c.filial_id || '')) return;
    const nome = (dados?.filiais || []).find(x => String(x.id) === String(filialId))?.nome;
    salvar({ tipo: 'colaborador', convenia_id: c.convenia_id, filial_id: filialId || null },
      filialId ? `${c.nome}: filial ${nome}` : `${c.nome}: sem filial`);
  };
  const salvarPadrao = (vinculo, valor) => {
    const v = valor.trim().replace(',', '.');
    const atual = dados?.padroes?.[vinculo];
    if ((v === '' && atual == null) || (v !== '' && Number(v) === Number(atual))) return;
    salvar({ tipo: 'padrao', vinculo, custo_dia: v === '' ? null : v },
      `Padrão de ${vinculo} ${v === '' ? 'removido' : 'salvo'}`);
  };

  const colaboradores = useMemo(() => {
    const lista = dados?.colaboradores || [];
    const b = busca.trim().toLowerCase();
    const tem = (valor, termo) => String(valor || '').toLowerCase().includes(termo.trim().toLowerCase());
    const soDigitos = (x) => String(x || '').replace(/\D/g, '');
    // filtro de valor: "226,10" (igual), ">200", "<=50", "100-300" (intervalo)
    const num = (t) => parseFloat(String(t).replace(/\./g, '').replace(',', '.'));
    const casaValor = (valor, termo) => {
      const t = String(termo || '').trim().replace(/\s|R\$/g, '');
      if (!t) return true;
      const v = Number(valor || 0);
      const intervalo = t.match(/^(\d[\d.,]*)-(\d[\d.,]*)$/);
      if (intervalo) return v >= num(intervalo[1]) - 0.005 && v <= num(intervalo[2]) + 0.005;
      const m = t.match(/^(>=|<=|>|<|=)?(\d[\d.,]*)$/);
      if (!m) return true;
      const n = num(m[2]);
      if (Number.isNaN(n)) return true;
      switch (m[1]) {
        case '>': return v > n;
        case '<': return v < n;
        case '>=': return v >= n - 0.005;
        case '<=': return v <= n + 0.005;
        default: return Math.abs(v - n) < 0.005;
      }
    };
    return lista.filter(c => {
      if (filtroVinculo && c.vinculo !== filtroVinculo) return false;
      if (b && ![c.nome, c.cpf, c.departamento, c.cargo, c.vinculo]
        .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      if (fcol.pago === 'sim' && !c.pagamento) return false;
      if (fcol.pago === 'nao' && c.pagamento) return false;
      if (fcol.cpf && !(tem(c.cpf, fcol.cpf)
        || (soDigitos(fcol.cpf) && soDigitos(c.cpf).includes(soDigitos(fcol.cpf))))) return false;
      if (fcol.nome && !tem(c.nome, fcol.nome)) return false;
      if (fcol.filial && (fcol.filial === '__sem' ? c.filial_id : String(c.filial_id || '') !== fcol.filial)) return false;
      if (fcol.admissao && !tem(c.admissao, fcol.admissao)) return false;
      if (fcol.depto && (c.departamento || '—') !== fcol.depto) return false;
      if (fcol.credito === 'com' && !(c.credito > 0)) return false;
      if (fcol.credito === 'sem' && c.credito > 0) return false;
      if (fcol.obs && !tem([c.obs, ...(c.alertas || []).map(a => a.texto)].join(' '), fcol.obs)) return false;
      if (!casaValor(c.custo_dia, fcol.v_custo_dia) || !casaValor(c.custo_mes, fcol.v_custo_mes)
        || !casaValor(c.dias_pagar, fcol.v_dias) || !casaValor(c.desconto, fcol.v_desconto)
        || !casaValor(c.total, fcol.v_total) || !casaValor(c.credito, fcol.v_credito)) return false;
      return true;
    });
  }, [dados, busca, filtroVinculo, fcol]);
  const deptosDaLista = useMemo(() => [...new Set((dados?.colaboradores || [])
    .map(c => c.departamento || '—'))].sort((a, b) => a.localeCompare(b, 'pt-BR')), [dados]);

  const totalCreditoFiltrado = colaboradores.reduce((s, c) => s + (c.credito || 0), 0);
  // benefício adicional da empresa (Mecanikids da Mecanizou): valor fixo, coluna própria
  const extraCfg = dados?.extra || null;
  const totalExtraFiltrado = colaboradores.reduce((s, c) => s + (c.extra || 0), 0);
  const [mexendoExtra, setMexendoExtra] = useState('');
  const salvarExtra = (c, marcar, valor) => {
    setMexendoExtra(c.convenia_id);
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/extra/${q}`,
      marcar ? { convenia_id: c.convenia_id, valor: valor ?? '' } : { convenia_id: c.convenia_id, remover: true },
      { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(true); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setMexendoExtra(''));
  };
  const comCredito = colaboradores.filter(c => c.credito > 0).length;
  const pagosFiltrado = colaboradores.filter(c => c.pagamento).length;
  const totalPagoFiltrado = colaboradores.reduce((s, c) => s + (c.pagamento?.valor || 0), 0);
  const faltamPagar = colaboradores.filter(c => c.credito > 0 && !c.pagamento);
  const comAlerta = colaboradores.filter(c => c.alertas?.length).length;
  const semCusto = colaboradores.filter(c => c.custo_dia == null && !c.desligado).length;

  // ─── acesso ao portal do VA (Caju, Flash, Swile...) — igual ao Ponto:
  //     link direto + login + senha revelada sob demanda (com log no backend) ───
  const [senhasPortal, setSenhasPortal] = useState({});   // {servico_id: senha}
  const copiar = (texto, rotulo) => {
    navigator.clipboard?.writeText(texto)
      .then(() => setMensagem(`✅ ${rotulo} copiado`))
      .catch(() => setMensagem('❌ Não foi possível copiar'));
  };
  const revelarSenha = (servicoId) => {
    if (senhasPortal[servicoId] != null) {
      copiar(senhasPortal[servicoId], 'Senha');
      return;
    }
    axios.get(`${API}/api/bporh-credencial/`,
      { params: { tipo: 'servico', id: servicoId, campo: 'senha' }, headers: headers() })
      .then((res) => {
        setSenhasPortal(s => ({ ...s, [servicoId]: res.data.valor || '' }));
        copiar(res.data.valor || '', 'Senha');
      })
      .catch(() => setMensagem('❌ Não foi possível obter a senha'));
  };
  useEffect(() => { setSenhasPortal({}); }, [clienteId]);

  // ─── pagamento: marca por colaborador (ou todos), guardando valor e dias
  //     do momento — é o histórico que o chat consulta depois ───
  const [salvandoPgto, setSalvandoPgto] = useState(false);
  const [confirmarPgto, setConfirmarPgto] = useState(false);
  const itemPgto = (c) => ({
    convenia_id: c.convenia_id, nome: c.nome, cpf: c.cpf, vinculo: c.vinculo,
    custo_dia: c.custo_dia, dias: c.dias_pagar, valor: c.credito,
  });
  const marcarPago = (lista, pago) => {
    if (travado) { setMensagem('🔒 Mês travado: reabra o mês para marcar ou desmarcar'); return; }
    const itens = lista.filter(c => pago ? c.credito > 0 : c.pagamento);
    if (!itens.length) {
      setMensagem(pago ? '⚠️ Ninguém com crédito para marcar' : '⚠️ Ninguém marcado como pago');
      return;
    }
    // resposta imediata na tela (otimista): o checkbox não espera o ida-e-volta
    const alvos = new Set(itens.map(c => c.convenia_id));
    const agora = new Date().toLocaleString('pt-BR',
      { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      .replace(',', '');
    setDados(d => d && ({
      ...d,
      colaboradores: d.colaboradores.map(c => (alvos.has(c.convenia_id)
        ? { ...c, pagamento: pago ? { valor: c.credito, dias: c.dias_pagar, pago_em: agora } : null }
        : c)),
    }));
    setSalvandoPgto(true);
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/pagar/${q}`,
      { mes, pago, colaboradores: itens.map(itemPgto) }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(true); })
      .catch((error) => {
        setMensagem(`❌ ${error.response?.data?.mensagem || 'Não foi possível registrar o pagamento'}`);
        carregar(true);   // deu errado: volta ao que está no banco
      })
      .finally(() => setSalvandoPgto(false));
  };

  // ─── exporta no LAYOUT DA PLATAFORMA (Caju, Flash...) para subir lá ───
  // cada plataforma tem o seu modelo; o backend monta o arquivo, aqui só
  // mandamos as linhas visíveis (CPF + crédito) e baixamos o retorno
  // cada plataforma de VA exige o seu modelo de arquivo (ext = só o fallback do
  // nome; o servidor manda o nome certo no Content-Disposition)
  const FORMATOS = [
    { id: 'caju', rotulo: 'Caju', ext: 'csv' },
    { id: 'flash', rotulo: 'Flash', ext: 'xlsx' },
    { id: 'pluxee', rotulo: 'Pluxee', ext: 'xlsx' },
    { id: 'swile', rotulo: 'Swile', ext: 'xlsx' },
  ];
  const provedorEmpresa = (dados?.portais || [])
    .map(p => (p.propriedade || '').trim().toLowerCase())
    .find(p => FORMATOS.some(f => p.includes(f.id)));
  // só o arquivo da plataforma DESTA empresa. Sem provedor reconhecido no
  // cadastro (ou provedor ainda sem layout, como a VR), mostra todos — melhor
  // escolher na mão do que ficar sem botão nenhum.
  const formatosDaEmpresa = FORMATOS.filter(f => provedorEmpresa?.includes(f.id));
  const formatosVisiveis = formatosDaEmpresa.length ? formatosDaEmpresa : FORMATOS;
  const [exportando, setExportando] = useState(false);

  // Data prevista do crédito (uma por empresa/competência). O input mostra a
  // gravada; sem ela, a sugerida pelo mês anterior — que só vira registro
  // quando o operador confirma.
  // ─── Boleto da recarga: anexa o PDF, monta o e-mail com o resumo por
  // colaborador e manda ao financeiro do cliente. Fica tudo no histórico. ───
  const [boletoAberto, setBoletoAberto] = useState(false);
  const [boletoDados, setBoletoDados] = useState(null);   // anexos + histórico + e-mails
  const [boletoPrevia, setBoletoPrevia] = useState(null);
  const [boletoForm, setBoletoForm] = useState({ emails: '', copia: '', vencimento: '',
    taxa_boleto: '' });
  const [boletoSel, setBoletoSel] = useState([]);         // ids anexados a enviar
  // quem assina o e-mail: o operador que atende o cliente (senha de app em
  // bporh_operador). Vem sugerido, mas dá para trocar.
  const [boletoOperador, setBoletoOperador] = useState('');
  const [subindoBoleto, setSubindoBoleto] = useState(false);
  const [enviandoBoleto, setEnviandoBoleto] = useState(false);
  const boletoInput = React.useRef(null);

  const carregarBoleto = React.useCallback(() => {
    if (!clienteId) return Promise.resolve();
    return axios.get(`${API}/api/bpo-beneficios/${clienteId}/boleto/${q}`,
      { params: { mes }, headers: headers() })
      .then((res) => {
        setBoletoDados(res.data);
        // O cadastro do cliente MANDA: preservar o que estava digitado fazia o
        // e-mail do financeiro do cliente anterior sobreviver à troca de empresa
        // (o seletor troca clienteId sem desmontar a tela) e o boleto ia para o
        // financeiro errado.
        setBoletoForm((f) => ({
          ...f,
          emails: res.data.financeiro_emails || '',
          copia: res.data.financeiro_copia || '',
        }));
        setBoletoSel((res.data.boletos || []).map(b => b.id));
        setBoletoOperador(String(res.data.operador_sugerido_id || ''));
      });
  }, [clienteId, mes]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Troca de cliente zera TUDO do boleto: destinatário, cópia, vencimento,
  // remetente, anexos selecionados e prévia. Sem isto, dado de um cliente
  // aparece (e é enviado) no contexto de outro.
  useEffect(() => {
    setBoletoAberto(false);
    setBoletoDados(null);
    setBoletoPrevia(null);
    setBoletoSel([]);
    setBoletoOperador('');
    setBoletoForm({ emails: '', copia: '', vencimento: '' });
    setVinculosExtra([]);
    setNovoVinculo(null);
  }, [clienteId]);

  const abrirBoleto = () => {
    setBoletoAberto(true);
    setBoletoPrevia(null);
    carregarBoleto().catch(() => setMensagem('Não foi possível carregar os boletos'));
  };

  const anexarBoleto = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('arquivo', file);
    fd.append('mes', mes);
    setSubindoBoleto(true);
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/boleto/${q}`, fd, { headers: headers() })
      .then((res) => {
        setBoletoDados((d) => ({ ...(d || {}), boletos: res.data.boletos || [] }));
        setBoletoSel((res.data.boletos || []).map(b => b.id));
        setMensagem(`${res.data.mensagem}`);
      })
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível anexar o boleto'}`))
      .finally(() => setSubindoBoleto(false));
  };

  const removerBoleto = (b) => {
    axios.delete(`${API}/api/bpo-beneficios/${clienteId}/boleto/${q}`,
      { params: { id: b.id }, headers: headers() })
      .then(() => carregarBoleto())
      .catch(() => setMensagem('Não foi possível remover o boleto'));
  };

  const salvarFinanceiro = () => {
    axios.patch(`${API}/api/bpo-beneficios/${clienteId}/editar/${q}`,
      { tipo: 'financeiro', emails: boletoForm.emails, copia: boletoForm.copia },
      { headers: headers() })
      .then((res) => setMensagem(`${res.data.mensagem}`))
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível salvar o e-mail'}`));
  };

  // Por que recebeu menos que o mês cheio? Os alertas da linha já sabem
  // (férias, afastamento, admissão/desligamento no meio do mês) — só faltava
  // levarem isso para o e-mail do cliente.
  const motivoDaLinha = (c) => {
    const partes = (c.alertas || [])
      .filter(a => ['ferias', 'afastamento', 'novo', 'desligado'].includes(a.tipo))
      .map(a => a.texto.replace(' — descontado', '').replace(' — proporcional', ''));
    if (c.dias_manual) {
      partes.push(`dias ajustados pelo RH: ${c.dias_pagar} de ${c.dias_uteis} dias úteis`);
    } else if (!partes.length && c.dias_pagar != null && c.dias_uteis != null
        && c.dias_pagar < c.dias_uteis) {
      partes.push(`${c.dias_pagar} de ${c.dias_uteis} dias úteis`);
    }
    return partes.join(' · ');
  };

  // dias daquela pessoa NESTA competência — corrige caso a caso (banco de
  // horas, escala, acerto combinado) sem mexer no custo/dia
  // Quem participa do benefício. Nem todo mundo do espelho da Convenia recebe
  // — o home office é acordado por contrato e cobre parte do quadro. Quem sai
  // some da lista e do total, mas fica num painel para voltar quando precisar.
  const [mexendoParticipante, setMexendoParticipante] = useState('');
  const [verExcluidos, setVerExcluidos] = useState(false);
  // confirmação com motivo — tirar alguém muda o valor do pedido do mês
  const [tirando, setTirando] = useState(null);
  const [motivoTirar, setMotivoTirar] = useState('');
  // incluir quem o filtro do mês deixou de fora (desligado antes, admitido
  // depois) — vem do espelho da Convenia em dados.candidatos
  const [incluindoAberto, setIncluindoAberto] = useState(false);
  const [buscaCandidato, setBuscaCandidato] = useState('');
  // `forcar` grava a linha em vez de apagá-la: é o que mantém na lista quem o
  // filtro do mês não traria sozinho
  const definirParticipa = (c, participa, motivo, forcar) => {
    setMexendoParticipante(c.convenia_id);
    axios.patch(`${API}/api/bpo-beneficios/${clienteId}/editar/${q}`,
      { tipo: 'participante', convenia_id: c.convenia_id, participa, motivo, forcar },
      { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível alterar'}`))
      .finally(() => setMexendoParticipante(''));
  };

  const salvarDias = (c, valor) => {
    const v = String(valor ?? '').trim();
    if (v !== '' && !/^\d{1,2}$/.test(v)) {
      setMensagem('⚠️ Dias deve ser um número de 0 a 31');
      return;
    }
    const accessToken = localStorage.getItem('access');
    axios.patch(`${API}/api/bpo-beneficios/${clienteId}/editar/${q}`,
      { tipo: 'dias', convenia_id: c.convenia_id, mes, dias: v },
      { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(true); })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível ajustar os dias'}`));
  };

  // o resumo do e-mail usa as MESMAS linhas do arquivo enviado à plataforma
  const linhasDoEmail = () => linhasDoArquivo.map(c => ({
    nome: c.nome, vinculo: c.vinculo, valor: c.credito, extra: c.extra || 0,
    motivo: motivoDaLinha(c),
  }));

  // ─── planilha de Contas a Pagar da Omie do VA (Gauss): só os pagos, uma conta por pessoa ───
  const [omieVa, setOmieVa] = useState(null);   // {vencimento, conta, categoria, departamentos, previa, carregando}
  const corpoOmieVa = (o, extra) => ({
    mes, vencimento: o.vencimento, conta_corrente: o.conta, categoria: o.categoria,
    departamentos: o.departamentos, ...extra,
  });
  const previaOmieVa = (o) => {
    setOmieVa((x) => ({ ...(x || o), carregando: true }));
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/omie/`, corpoOmieVa(o), { headers: headers(), timeout: 180000 })
      .then((r) => setOmieVa((x) => ({ ...(x || o), carregando: false, previa: r.data,
        conta: (x || o).conta || r.data.conta_corrente })))
      .catch((e) => { setOmieVa((x) => x && ({ ...x, carregando: false }));
        setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível montar a planilha'}`); });
  };
  const abrirOmieVa = () => {
    const o = { vencimento: boletoForm.vencimento || '', conta: '', categoria: 'Vale Refeição',
      departamentos: {}, previa: null };
    setOmieVa(o);
    previaOmieVa(o);
  };
  const mudarOmieVa = (campo, valor) => setOmieVa((x) => ({ ...x, [campo]: valor }));
  const baixarOmieVa = () => {
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/omie/`, corpoOmieVa(omieVa, { baixar: true }),
      { headers: headers(), responseType: 'blob', timeout: 180000 })
      .then((r) => {
        const m = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(r.data);
        a.download = m ? m[1] : `Omie_Contas_Pagar_VA_${mes}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(async (e) => {
        let msg = 'Não foi possível gerar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém */ }
        setMensagem(`❌ ${msg}`);
      });
  };
  // anexar a planilha Omie ao e-mail do boleto: ligado por padrão onde a Importação Omie existe
  const anexarOmie = tipo === 'va' && (boletoForm.anexar_omie ?? !!dados?.omie_configurada);

  const previaBoleto = () => {
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/boleto/email/${q}`, {
      mes, colaboradores: linhasDoEmail(), extra_nome: extraCfg?.nome, boleto_ids: boletoSel,
      destinatarios: boletoForm.emails, copia: boletoForm.copia,
      vencimento: boletoForm.vencimento,
      taxa_boleto: boletoForm.taxa_boleto, anexar_omie: anexarOmie,
      operador_id: boletoOperador || undefined, enviar: false,
    }, { headers: headers() })
      .then((res) => setBoletoPrevia(res.data.previa))
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível montar o e-mail'}`));
  };

  const enviarBoleto = () => {
    setEnviandoBoleto(true);
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/boleto/email/${q}`, {
      mes, colaboradores: linhasDoEmail(), extra_nome: extraCfg?.nome, boleto_ids: boletoSel,
      destinatarios: boletoForm.emails, copia: boletoForm.copia,
      vencimento: boletoForm.vencimento,
      taxa_boleto: boletoForm.taxa_boleto, anexar_omie: anexarOmie,
      assunto: boletoPrevia?.assunto, corpo_html: boletoPrevia?.corpo_html,
      operador_id: boletoOperador || undefined, enviar: true,
    }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setBoletoPrevia(null);
        carregarBoleto();
      })
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setEnviandoBoleto(false));
  };

  // ─── Calendário de feriados: o que reduz os dias úteis do mês ───
  // Nacionais vêm da BrasilAPI; municipais e estaduais são cadastrados aqui,
  // porque a API da Convenia não expõe o calendário dela.
  const [feriadosAberto, setFeriadosAberto] = useState(false);
  const [feriadosAno, setFeriadosAno] = useState(null);
  const [carregandoFeriados, setCarregandoFeriados] = useState(false);
  const [feriadoNovo, setFeriadoNovo] = useState(null);   // {data, nome, tipo, geral}
  const anoDoMes = Number((mes || '').slice(0, 4)) || new Date().getFullYear();

  const carregarFeriados = React.useCallback(() => {
    setCarregandoFeriados(true);
    axios.get(`${API}/api/bpo/feriados/`,
      { params: { ano: anoDoMes, cliente_id: clienteId }, headers: headers() })
      .then((res) => setFeriadosAno(res.data))
      .catch(() => setMensagem('Não foi possível carregar o calendário'))
      .finally(() => setCarregandoFeriados(false));
  }, [anoDoMes, clienteId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const abrirFeriados = () => {
    setFeriadosAberto(true);
    setFeriadoEdit(null);
    setFeriadoNovo({ data: `${anoDoMes}-01-01`, nome: '', tipo: 'municipal', geral: false });
    carregarFeriados();
  };

  // recarrega a tela junto: mexer no feriado muda os dias úteis e o crédito
  const aposMexerNoFeriado = (msg) => {
    setMensagem(msg);
    carregarFeriados();
    carregar(true);
  };

  const salvarFeriado = () => {
    axios.post(`${API}/api/bpo/feriados/`, {
      data: feriadoNovo.data, nome: feriadoNovo.nome, tipo: feriadoNovo.tipo,
      cliente_id: feriadoNovo.geral ? null : clienteId,
    }, { headers: headers() })
      .then((res) => {
        aposMexerNoFeriado(`${res.data.mensagem}`);
        setFeriadoNovo(f => ({ ...f, nome: '' }));
      })
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível cadastrar'}`));
  };

  // edição inline na tabela do calendário: {id, data(iso), nome, tipo, geral}
  const [feriadoEdit, setFeriadoEdit] = useState(null);
  const [salvandoFeriado, setSalvandoFeriado] = useState(false);

  const salvarEdicaoFeriado = () => {
    if (!feriadoEdit?.nome?.trim() || !feriadoEdit?.data) return;
    setSalvandoFeriado(true);
    axios.patch(`${API}/api/bpo/feriados/`, {
      id: feriadoEdit.id, data: feriadoEdit.data,
      nome: feriadoEdit.nome.trim(), tipo: feriadoEdit.tipo,
      cliente_id: feriadoEdit.geral ? null : clienteId,
    }, { headers: headers() })
      .then((res) => {
        aposMexerNoFeriado(`${res.data.mensagem}`);
        setFeriadoEdit(null);
      })
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível salvar a edição'}`))
      .finally(() => setSalvandoFeriado(false));
  };

  const removerFeriado = (fer) => {
    axios.delete(`${API}/api/bpo/feriados/`,
      { params: { id: fer.id }, headers: headers() })
      .then((res) => aposMexerNoFeriado(`${res.data.mensagem}`))
      .catch(() => setMensagem('Não foi possível remover'));
  };

  const importarNacionais = () => {
    axios.post(`${API}/api/bpo/feriados/`, { importar_ano: anoDoMes },
      { headers: headers() })
      .then((res) => aposMexerNoFeriado(`${res.data.mensagem}`))
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível importar'}`));
  };

  // desmarcar em lote apaga registros de pagamento — confirma antes
  const [confirmandoDesmarcar, setConfirmandoDesmarcar] = useState(false);
  useEffect(() => { setConfirmandoDesmarcar(false); }, [clienteId, mes]);

  const [creditoEdit, setCreditoEdit] = useState(null);   // null = espelha o servidor
  const [salvandoCredito, setSalvandoCredito] = useState(false);
  useEffect(() => { setCreditoEdit(null); }, [clienteId, mes]);
  const creditoData = creditoEdit !== null
    ? creditoEdit
    : (dados?.credito_data || dados?.credito_data_sugerida || '');

  const salvarCreditoData = (valor) => {
    setCreditoEdit(valor);
    setSalvandoCredito(true);
    axios.patch(`${API}/api/bpo-beneficios/${clienteId}/editar/${q}`,
      { tipo: 'credito', mes, data: valor }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(true); })
      .catch((err) => {
        setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar a data do crédito'}`);
        setCreditoEdit(null);        // volta ao que está no servidor
      })
      .finally(() => setSalvandoCredito(false));
  };

  // O arquivo da plataforma leva SÓ os colaboradores marcados na tabela.
  // Sem nenhum marcado, vale a tabela filtrada inteira — senão o botão pareceria
  // quebrado para quem só quer o arquivo do mês fechado.
  const exportaveis = colaboradores.filter(c => (c.credito > 0 || c.extra > 0) && c.cpf);
  const marcados = exportaveis.filter(c => c.pagamento);
  const linhasDoArquivo = marcados.length ? marcados : exportaveis;

  const exportarPlataforma = (formato) => {
    // convenia_id vai junto: layouts como o da Pluxee precisam do cadastro
    // completo, que o backend busca no espelho (não sai do browser)
    const itens = linhasDoArquivo.map(c => ({
      convenia_id: c.convenia_id, cpf: c.cpf, nome: c.nome, valor: c.credito, extra: c.extra || 0,
    }));
    if (!itens.length) { setMensagem('⚠️ Nenhum colaborador com CPF e crédito para exportar'); return; }
    setExportando(true);
    axios.post(`${API}/api/bpo-beneficios/${clienteId}/exportar/${q}`,
      { formato, mes, colaboradores: itens },
      { headers: headers(), responseType: 'blob' })
      .then((res) => {
        const ext = FORMATOS.find(f => f.id === formato)?.ext || 'csv';
        const nome = (res.headers['content-disposition'] || '')
          .match(/filename="?([^"]+)"?/)?.[1]
          || `${formato}_${mes}.${ext}`;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = nome;
        a.click();
        URL.revokeObjectURL(a.href);
        setMensagem(`✅ Arquivo ${formato.toUpperCase()} gerado com ${itens.length} colaborador(es)`
          + (marcados.length ? ' (só os marcados)' : ''));
      })
      .catch(async (error) => {
        // responseType blob: o corpo do erro TAMBÉM vem como Blob, então a
        // mensagem do backend (quem está faltando o quê) precisa ser lida daqui
        // — sem isto o operador só via um erro genérico e não sabia o motivo
        let msg = 'Não foi possível gerar o arquivo da plataforma';
        try {
          const texto = await error.response?.data?.text?.();
          const corpo = texto ? JSON.parse(texto) : null;
          if (corpo?.mensagem) msg = corpo.mensagem;
        } catch (e) { /* corpo não era JSON: fica a genérica */ }
        setMensagem(`❌ ${msg}`);
      })
      .finally(() => setExportando(false));
  };

  // exporta a tabela filtrada no formato da planilha (CSV com ;)
  const exportarCsv = () => {
    const cab = ['CPF', 'NOME', 'VINCULO', 'ADMISSAO', 'DEPARTAMENTO',
      'CUSTO DIA', 'DIAS', 'DESC', 'TOTAL', 'CREDITO', 'OBS', 'PAGO EM'];
    const fmt = (v) => (v == null ? '' : String(v).replace(/;/g, ','));
    const linhas = colaboradores.map(c => [
      c.cpf, c.nome, c.vinculo, c.admissao, c.departamento,
      c.custo_dia != null ? String(c.custo_dia).replace('.', ',') : '',
      c.dias_pagar,
      c.desconto ? String(c.desconto).replace('.', ',') : '',
      c.total ? String(c.total).replace('.', ',') : '',
      c.credito ? String(c.credito).replace('.', ',') : '',
      [c.obs, ...(c.alertas || []).map(a => a.texto)].filter(Boolean).join(' | '),
      c.pagamento?.pago_em || '',
    ].map(fmt).join(';'));
    const blob = new Blob(['﻿' + [cab.join(';'), ...linhas].join('\r\n')],
      { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `beneficios_${(dados?.cliente || 'empresa').replace(/\s+/g, '_')}_${mes}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // vínculos do "Padrão por vínculo": os de quem está na lista do mês + os que já
  // têm padrão salvo + os incluídos agora pelo botão "+ Vínculo"
  const vinculosPadrao = [...new Set([
    ...(dados?.vinculos || []),
    ...Object.keys(dados?.padroes || {}).filter((k) => dados.padroes[k] != null),
    ...Object.keys(dados?.padroes_mes || {}),
    ...vinculosExtra,
  ])];

  // cliente que paga em ATRASO (home office da Orulo): o mês da tela é o do
  // home office e da folha; o colaborador recebe no mês seguinte (29/09/2026)
  const emAtraso = Boolean(dados?.pagamento_atrasado);
  const mesPago = mes;
  const mesRecebe = dados?.mes_pagamento || mesDepois(mes);
  const rotRef = emAtraso ? 'home office de' : 'crédito de';

  return (
    <div className="bporh-admissoes-page">
      {configAberto && clienteId && (
        <ConfigBeneficioModal tipo={tipo} clienteId={clienteId} mes={mes} cliente={dados?.cliente}
          onFechar={() => setConfigAberto(false)} onSalvo={() => carregar(true)} />
      )}
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho (mesmo padrão das irmãs) */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <i className={`fas ${rotulo.icone} header-icon me-2`}></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — {rotulo.titulo}
              {dados?.cliente && <span className="text-muted"> · {dados.cliente}</span>}
            </h2>
            {/* mês-base do crédito (comprado adiantado) sempre à vista */}
            <span className="badge d-inline-flex align-items-center gap-2"
              style={{ background: '#00205b', fontSize: '.9rem', padding: '.5rem .8rem' }}
              title="O VA é pago no mês escolhido e o colaborador usa no mês seguinte; o desconto sai na folha do mês do pagamento">
              <i className="fas fa-calendar-days"></i>
              {rotRef} <b style={{ textTransform: 'capitalize' }}>{mesExtenso(dados?.mes_uso || mesDepois(mes))}</b>
              <span style={{ opacity: .85 }}>
                {emAtraso
                  ? <> · colaborador recebe em <b style={{ textTransform: 'capitalize' }}>{mesExtenso(mesRecebe)}</b></>
                  : <> · pago em <b style={{ textTransform: 'capitalize' }}>{mesExtenso(mesPago)}</b></>}
                {mes === mesAtual() && (
                  <span style={{ color: '#f1be48', fontWeight: 700 }}> · este mês</span>
                )}
                {' '}· {tipo === 'home_office' ? 'na folha de' : 'desconto na folha de'} <b style={{ textTransform: 'capitalize' }}>{mesExtenso(mesPago)}</b>
                {emAtraso && <span style={{ color: '#f1be48', fontWeight: 700 }}> · pago em atraso</span>}
              </span>
            </span>
            {/* data em que o crédito deve cair no cartão, editável ali mesmo */}
            {clienteId && dados && (
              <span className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded"
                style={{ background: '#f2f4f8', border: '1px solid #dee2e6' }}
                title={creditoData
                  ? 'Data prevista para o crédito cair no cartão do colaborador'
                  : 'Defina a data prevista do crédito desta competência'}>
                <i className="fas fa-credit-card text-muted" style={{ fontSize: '.85rem' }}></i>
                <span className="small text-muted">crédito em</span>
                <input type="date" className="form-control form-control-sm"
                  style={{ width: 'auto' }}
                  value={creditoData}
                  onChange={(e) => salvarCreditoData(e.target.value)}
                  disabled={salvandoCredito}
                  aria-label="Data prevista do crédito" />
                {salvandoCredito && <span className="spinner-border spinner-border-sm"></span>}
                {!dados.credito_data && dados.credito_data_sugerida && (
                  <span className="small" style={{ color: '#8a6d00' }}
                    title="Sugerido pelo dia do mês anterior — confirme para gravar">
                    sugerido
                  </span>
                )}
                {creditoData && (
                  <button type="button" className="btn btn-sm btn-light border p-1"
                    onClick={() => salvarCreditoData('')} disabled={salvandoCredito}
                    title="Limpar a data do crédito">
                    <i className="fas fa-times text-danger" style={{ fontSize: '.7rem' }}></i>
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteIdRota ? (
              <Link to="/BpoRhClientesCadastro" className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-arrow-left me-2"></i>Clientes
              </Link>
            ) : (
              <>
                {clienteSel && (
                  <button className="btn btn-outline-secondary btn-sm"
                    onClick={() => setClienteSel('')}
                    title="Voltar para a lista de portais de VA">
                    <i className="fas fa-arrow-left me-2"></i>Portais
                  </button>
                )}
                <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: '220px' }}
                  value={clienteSel} onChange={(e) => setClienteSel(e.target.value)}>
                  <option value="">Selecione a empresa...</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.cliente}</option>)}
                </select>
              </>
            )}
            <input type="month" className="form-control form-control-sm" style={{ width: 'auto' }}
              value={mes} onChange={(e) => setMes(e.target.value)} />
            {clienteId && dados && (
              <button className="btn btn-outline-dark btn-sm" onClick={() => setConfigAberto(true)}
                title={`Valor mensal por vínculo/filial e dias que contam (hoje: ${dados.regra_dias_rotulo || 'dias úteis'})`}>
                <i className="fas fa-sliders me-2"></i>Configurar
              </button>
            )}
            <button className="btn btn-outline-primary btn-sm" onClick={() => carregar()}
              disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
            </button>
            <button className="btn btn-outline-success btn-sm" onClick={exportarCsv}
              disabled={!colaboradores.length}
              title="Baixar a tabela filtrada em CSV (formato da planilha de controle)">
              <i className="fas fa-file-csv me-2"></i>Exportar
            </button>
            {clienteId && dados && tipo === 'va' && (
              <button className="btn btn-outline-primary btn-sm" onClick={abrirOmieVa}
                disabled={!marcados.length}
                title={marcados.length ? 'Planilha de Contas a Pagar da Omie com os colaboradores marcados como pagos'
                  : 'Marque como pago quem recebeu para gerar a planilha da Omie'}>
                <i className="fas fa-file-export me-2"></i>Planilha Omie
              </button>
            )}
            {clienteId && dados && formatosVisiveis.map(fm => {
              // sem provedor reconhecido, todos aparecem — aí o tooltip avisa
              const identificado = formatosDaEmpresa.length > 0;
              return (
              <button key={fm.id} className="btn btn-sm"
                style={{ background: '#f1be48', color: '#3a2c00', fontWeight: 600 }}
                onClick={() => exportarPlataforma(fm.id)}
                disabled={exportando || !linhasDoArquivo.length}
                title={(marcados.length
                  ? `Gera o arquivo do ${fm.rotulo} com os ${marcados.length} colaborador(es) marcados`
                  : `Gera o arquivo do ${fm.rotulo} com todos os ${exportaveis.length} com CPF e crédito`
                    + ' — marque na tabela para exportar só alguns')
                  + (identificado ? ''
                    : ' · plataforma de VA não identificada no cadastro desta empresa —'
                      + ' confira antes de subir')}>
                <i className={`fas ${exportando ? 'fa-spinner fa-spin' : 'fa-file-arrow-down'} me-2`}></i>
                Arquivo {fm.rotulo}
                {linhasDoArquivo.length > 0 && (
                  <span className="ms-2 badge"
                    style={{ background: 'rgba(0,0,0,.12)', color: 'inherit', fontWeight: 700 }}>
                    {linhasDoArquivo.length}
                  </span>
                )}
              </button>
              );
            })}
            {clienteId && dados && (
              <button className="btn btn-success btn-sm"
                onClick={() => setConfirmarPgto(true)}
                disabled={travado || salvandoPgto || !faltamPagar.length}
                title={faltamPagar.length
                  ? `Marcar ${faltamPagar.length} colaborador(es) como pagos em ${mesExtenso(mes)}`
                  : 'Todos os créditos deste filtro já estão marcados como pagos'}>
                <i className={`fas ${salvandoPgto ? 'fa-spinner fa-spin' : 'fa-circle-check'} me-2`}></i>
                Marcar todos como pagos
              </button>
            )}
            {clienteId && dados && (
              <button className="btn btn-sm"
                style={{ background: '#00205b', color: '#fff', fontWeight: 600 }}
                onClick={abrirBoleto}
                title="Anexar o boleto da recarga e enviar ao financeiro do cliente">
                <i className="fas fa-file-invoice-dollar me-2"></i>Boleto
              </button>
            )}
            {/* desmarcar apaga os registros de pagamento do mês: confirma antes */}
            {clienteId && dados && (confirmandoDesmarcar ? (
              <span className="d-inline-flex align-items-center gap-2">
                <span className="small text-danger fw-bold">
                  <i className="fas fa-triangle-exclamation me-1"></i>
                  Apagar o pagamento de {marcados.length} em {mesExtenso(mes)}?
                </span>
                <button className="btn btn-sm btn-danger" disabled={salvandoPgto}
                  onClick={() => {
                    marcarPago(marcados, false);
                    setConfirmandoDesmarcar(false);
                  }}>
                  <i className="fas fa-eraser me-1"></i>Confirmar
                </button>
                <button className="btn btn-sm btn-secondary" disabled={salvandoPgto}
                  onClick={() => setConfirmandoDesmarcar(false)}>
                  Cancelar
                </button>
              </span>
            ) : (
              <button className="btn btn-outline-danger btn-sm"
                onClick={() => setConfirmandoDesmarcar(true)}
                disabled={travado || salvandoPgto || !marcados.length}
                title={marcados.length
                  ? `Desmarcar os ${marcados.length} colaborador(es) marcados em ${mesExtenso(mes)}`
                  : 'Nenhum colaborador marcado neste filtro'}>
                <i className="fas fa-eraser me-2"></i>
                Desmarcar todos
              </button>
            ))}
          </div>
        </div>

        {!clienteId ? (
          /* entrada: os VAs de todas as empresas (formato /BpoRh/Benefícios) —
             abra o portal daqui ou clique em Controle para montar o mês */
          <div className="table-responsive bg-white rounded shadow-sm">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th><i className="fas fa-building me-2"></i>Cliente</th>
                  <th><i className="fas fa-user-tie me-2"></i>Operador</th>
                  <th><i className="fas fa-tv me-2"></i>Propriedade</th>
                  <th className="text-center"><i className="fas fa-globe me-2"></i>Portal</th>
                  <th><i className="fas fa-user me-2"></i>Login</th>
                  <th><i className="fas fa-key me-2"></i>Senha</th>
                  <th><i className="fas fa-envelope me-2"></i>E-mail reset</th>
                  <th className="text-center">Controle</th>
                </tr>
              </thead>
              <tbody>
                {portaisGeral.map((s) => (
                  <tr key={s.id}>
                    <td className="fw-bold">{s.cliente}</td>
                    <td>{s.operador || '-'}</td>
                    <td>{s.propriedade}</td>
                    <td className="text-center">
                      {s.portal ? (
                        <a href={s.portal} target="_blank" rel="noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title={`Abrir o portal ${s.propriedade} em nova aba`}>
                          <i className="fas fa-arrow-up-right-from-square me-1"></i>Abrir
                        </a>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      {s.login ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <span className="small">{s.login}</span>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => copiar(s.login, 'Login')} title="Copiar login">
                            <i className="fas fa-copy text-muted" style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td>
                      {s.tem_senha ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <code>{senhasPortal[s.id] != null ? senhasPortal[s.id] : '••••••••'}</code>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => revelarSenha(s.id)}
                            title="Revelar e copiar a senha (o acesso fica registrado)">
                            <i className={`fas ${senhasPortal[s.id] != null ? 'fa-copy' : 'fa-eye'} text-muted`}
                              style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    <td className="small">{s.email_reset || '-'}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-outline-success"
                        onClick={() => setClienteSel(String(s.cliente_id))}
                        title={`Montar o controle de VA da ${s.cliente} (colaboradores, dias úteis, crédito)`}>
                        <i className="fas fa-utensils me-1"></i>Controle
                      </button>
                    </td>
                  </tr>
                ))}
                {portaisGeral.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">
                    <i className="fas fa-inbox me-2"></i>
                    Nenhum VA cadastrado — cadastre o serviço (categoria Benefícios) na página Clientes.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Montando o controle de benefícios...</p>
          </div>
        ) : !dados ? (
          <div className="alert alert-danger">Não foi possível carregar os benefícios desta empresa.</div>
        ) : (
          <>
            {/* Feriados do mês num painel próprio à direita: junto dos outros
                badges eles se perdiam, e é a informação que explica por que o
                número de dias úteis caiu. */}
            {(dados.feriados || []).length > 0 && (
              <div className="d-flex justify-content-end mb-2">
                <button type="button"
                  className="bg-white rounded shadow-sm px-3 py-2 text-start border-0"
                  onClick={abrirFeriados}
                  title="Abrir o calendário para adicionar ou remover feriados"
                  style={{ border: '1px solid #f0dfa8', borderLeft: '4px solid #f1be48',
                    minWidth: '320px', cursor: 'pointer' }}>
                  <div className="small fw-bold mb-1 d-flex justify-content-between align-items-center"
                    style={{ color: '#8a6d00' }}>
                    <span>
                      <i className="fas fa-umbrella-beach me-2"></i>
                      Feriados de {mesExtenso(mes)}
                    </span>
                    <span style={{ fontSize: '.78rem', fontWeight: 500 }}>
                      <i className="fas fa-pen me-1"></i>editar
                    </span>
                  </div>
                  {(dados.feriados || []).map(fer => (
                    <div key={fer.data_iso}
                      className="d-flex align-items-center justify-content-between gap-3 small"
                      style={{ color: fer.desconta ? '#212529' : '#adb5bd' }}>
                      <span>
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fer.data}</b>
                        <span className="text-muted"> ({fer.dia_semana})</span>{' '}
                        {fer.nome}
                        {fer.cidade && (
                          <span className="text-muted" style={{ fontSize: '.85em' }}>
                            {' '}· {fer.cidade}
                          </span>
                        )}
                      </span>
                      <span className="badge" style={fer.desconta
                        ? { background: '#fff5da', color: '#8a6d00', border: '1px solid #f0dfa8' }
                        : { background: '#f2f4f8', color: '#6c757d', border: '1px solid #dee2e6' }}>
                        {fer.desconta ? '−1 dia' : 'fim de semana'}
                      </span>
                    </div>
                  ))}
                </button>
              </div>
            )}

            {/* sem feriado no mês o painel some — o atalho fica no botão abaixo */}
            {!(dados.feriados || []).length && (
              <div className="d-flex justify-content-end mb-2">
                <button type="button" className="btn btn-sm btn-light border"
                  onClick={abrirFeriados}>
                  <i className="fas fa-umbrella-beach me-2" style={{ color: '#8a6d00' }}></i>
                  Nenhum feriado em {mesExtenso(mes)} — abrir calendário
                </button>
              </div>
            )}

            <TravaBeneficio clienteId={clienteId} tipo={tipo} mes={mes} trava={dados.trava}
              onMudou={() => carregar(true)} />

            {/* resumo */}
            <div className="d-flex gap-2 flex-wrap mb-3">
              <span className="badge fs-6" style={{ background: '#00205b' }}>
                <i className="fas fa-calendar-days me-1"></i>
                {dados.dias_corridos} dias · <b>{dados.dias_uteis} úteis</b>
                <span style={{ opacity: .85 }}> em {mesExtenso(dados.mes_uso || mesDepois(mes))}</span>
              </span>
              <span className="badge bg-secondary fs-6">{colaboradores.length} colaboradores</span>
              <span className="badge fs-6" style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #1fa971' }}>
                crédito total {brl(totalCreditoFiltrado)}
              </span>
              {extraCfg && (
                <span className="badge fs-6" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}
                  title={`${extraCfg.nome}: ${brl(extraCfg.valor_padrao)} fixo por pessoa marcada — vai em coluna própria no e-mail e no arquivo`}>
                  {extraCfg.nome} {brl(totalExtraFiltrado)} · pedido {brl(totalCreditoFiltrado + totalExtraFiltrado)}
                </span>
              )}
              <span className="badge fs-6" style={{
                background: pagosFiltrado === comCredito && comCredito > 0 ? '#116b47' : '#f2fbf6',
                color: pagosFiltrado === comCredito && comCredito > 0 ? '#fff' : '#116b47',
                border: '1px solid #1fa971' }}
                title={`Pago: ${brl(totalPagoFiltrado)}`}>
                <i className="fas fa-circle-check me-1"></i>
                {pagosFiltrado}/{comCredito} pagos
              </span>
              {/* subtotal dos marcados na tabela (pedido de 29/09/2026): confere o
                  valor do arquivo/boleto antes de gerar, sem somar à mão */}
              <span className="badge fs-6" style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}
                title="Soma dos colaboradores marcados (checkbox) com os filtros atuais">
                <i className="fas fa-square-check me-1"></i>
                subtotal selecionados {brl(totalPagoFiltrado)}
                {pagosFiltrado > 0 && <span style={{ opacity: .8 }}> · {pagosFiltrado} colab.</span>}
              </span>
              {comAlerta > 0 && (
                <span className="badge fs-6" style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}>
                  <i className="fas fa-triangle-exclamation me-1"></i>{comAlerta} com alerta
                </span>
              )}
              {semCusto > 0 && (
                <span className="badge fs-6" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }}
                  title="Defina o custo/dia do vínculo (abaixo) ou um valor individual na linha">
                  {semCusto} sem custo definido
                </span>
              )}
            </div>

            {/* portal do benefício no cliente (Caju, Flash, Swile, VR...) */}
            {(dados.portais || []).length > 0 && (
              <div className="bg-white rounded shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-arrow-up-right-from-square me-2" style={{ color: '#00b5cc' }}></i>
                  Portal do {rotulo.curto}
                </h6>
                <div className="d-flex flex-column gap-2">
                  {dados.portais.map(p => (
                    <div key={p.id} className="d-flex align-items-center gap-2 flex-wrap">
                      {/* sem URL cadastrada o botão viraria link morto: mostra a
                          operadora e diz onde preencher */}
                      {p.portal ? (
                        <a href={p.portal} target="_blank" rel="noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          title={`Abrir o portal ${p.propriedade} em nova aba`}>
                          <i className="fas fa-arrow-up-right-from-square me-2"></i>{p.propriedade}
                        </a>
                      ) : (
                        <span className="btn btn-sm btn-light border disabled"
                          title="Este serviço está sem endereço do portal — cadastre em BPO RH → Serviços">
                          <i className="fas fa-link-slash me-2 text-muted"></i>{p.propriedade}
                          <span className="small text-muted ms-2">sem portal cadastrado</span>
                        </span>
                      )}
                      {p.login && (
                        <span className="small text-muted d-inline-flex align-items-center gap-1">
                          <i className="fas fa-user"></i>
                          <code style={{ color: '#00205b' }}>{p.login}</code>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => copiar(p.login, 'Login')} title="Copiar login/referência">
                            <i className="fas fa-copy text-muted" style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      )}
                      {p.tem_senha && (
                        <span className="small text-muted d-inline-flex align-items-center gap-1">
                          <i className="fas fa-key"></i>
                          <code>{senhasPortal[p.id] != null ? senhasPortal[p.id] : '••••••••'}</code>
                          <button className="btn btn-sm btn-light p-1 border"
                            onClick={() => revelarSenha(p.id)}
                            title="Revelar e copiar a senha (o acesso fica registrado)">
                            <i className={`fas ${senhasPortal[p.id] != null ? 'fa-copy' : 'fa-eye'} text-muted`}
                              style={{ fontSize: '.72rem' }}></i>
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* custo por FILIAL — só aparece em cliente com filiais cadastradas na folha */}
            {(dados.filiais || []).length > 0 && (
              <div className="bg-white rounded shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid #0a6ea1' }}>
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-building me-2" style={{ color: '#0a6ea1' }}></i>
                  Padrão por filial
                  <span className="text-muted small fw-normal ms-2">
                    vale para quem está na filial (coluna Filial da tabela); prevalece sobre o vínculo,
                    e o valor individual da linha prevalece sobre os dois
                  </span>
                </h6>
                <div className="d-flex gap-3 flex-wrap align-items-center">
                  {dados.filiais.map(fl => {
                    const pf = dados.padroes_filial?.[String(fl.id)] || {};
                    return (
                      <div key={fl.id} className="d-flex align-items-center gap-2">
                        <span className="fw-semibold small">{fl.nome}</span>
                        <div className="input-group input-group-sm" style={{ width: '128px' }}>
                          <span className="input-group-text px-1">R$</span>
                          <input type="number" step="0.01" min="0"
                            className={`form-control text-end ${pf.custo_dia != null ? 'fw-bold' : ''}`}
                            style={pf.custo_dia != null ? { background: '#e3f2fd' } : undefined}
                            key={`fd-${fl.id}-${pf.custo_dia ?? ''}`}
                            defaultValue={pf.custo_dia ?? ''}
                            placeholder="0,00"
                            title="Custo por dia útil nesta filial"
                            onBlur={(e) => salvarFilialValor(fl, 'custo_dia', e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                          <span className="input-group-text px-1 small text-muted">/dia</span>
                        </div>
                        <div className="input-group input-group-sm" style={{ width: '138px' }}>
                          <span className="input-group-text px-1">R$</span>
                          <input type="number" step="0.01" min="0"
                            className={`form-control text-end ${pf.custo_mes != null ? 'fw-bold' : ''}`}
                            style={pf.custo_mes != null ? { background: '#e6f7ef' } : undefined}
                            key={`fm-${fl.id}-${pf.custo_mes ?? ''}`}
                            defaultValue={pf.custo_mes ?? ''}
                            placeholder="fixo"
                            title="Valor FIXO mensal nesta filial: preenchido, manda no lugar do custo/dia"
                            onBlur={(e) => salvarFilialValor(fl, 'custo_mes', e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                          <span className="input-group-text px-1 small text-muted">/mês</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* custo/dia padrão por vínculo (editável) */}
            <div className="bg-white rounded shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid #f1be48' }}>
              <h6 className="fw-bold mb-2">
                <i className="fas fa-sliders me-2" style={{ color: '#8a6d00' }}></i>
                Padrão por vínculo
                <span className="text-muted small fw-normal ms-2">
                  custo por dia útil <b>ou</b> valor fixo mensal — vale para todos do vínculo;
                  o valor individual na linha prevalece
                </span>
              </h6>
              <div className="d-flex gap-3 flex-wrap align-items-center">
                {vinculosPadrao.map(v => (
                  <div key={v} className="d-flex align-items-center gap-2">
                    <span className="fw-semibold small">{v}</span>
                    <div className="input-group input-group-sm" style={{ width: '128px' }}
                      title="Custo por dia útil do vínculo">
                      <span className="input-group-text px-1">R$</span>
                      <input disabled={travado} type="number" step="0.01" min="0"
                        className="form-control text-end"
                        key={`${v}-${dados.padroes?.[v] ?? ''}`}
                        defaultValue={dados.padroes?.[v] ?? ''}
                        placeholder="0,00"
                        onBlur={(e) => salvarPadrao(v, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                      <span className="input-group-text px-1 small text-muted">/dia</span>
                    </div>
                    <div className="input-group input-group-sm" style={{ width: '138px' }}
                      title="Valor FIXO mensal do vínculo (Rubeus e afins): preenchido, manda no lugar do custo/dia">
                      <span className="input-group-text px-1">R$</span>
                      <input disabled={travado} type="number" step="0.01" min="0"
                        className={`form-control text-end ${dados.padroes_mes?.[v] != null ? 'fw-bold' : ''}`}
                        style={dados.padroes_mes?.[v] != null ? { background: '#e6f7ef' } : undefined}
                        key={`m-${v}-${dados.padroes_mes?.[v] ?? ''}`}
                        defaultValue={dados.padroes_mes?.[v] ?? ''}
                        placeholder="fixo"
                        onBlur={(e) => salvarPadraoFixo(v, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                      <span className="input-group-text px-1 small text-muted">/mês</span>
                    </div>
                  </div>
                ))}
                {novoVinculo === null ? (
                  <button disabled={travado} type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => setNovoVinculo('')}
                    title="Incluir o padrão de um vínculo que ainda não aparece aqui">
                    <i className="fas fa-plus me-1"></i>Vínculo
                  </button>
                ) : (
                  <div className="d-flex align-items-center gap-1">
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} autoFocus
                      value={VINCULOS_CONVENIA.includes(novoVinculo) || novoVinculo === '' ? novoVinculo : '__outro'}
                      onChange={(e) => setNovoVinculo(e.target.value === '__outro' ? ' ' : e.target.value)}
                      aria-label="Vínculo a incluir">
                      <option value="">Escolha o vínculo...</option>
                      {VINCULOS_CONVENIA.filter(x => !vinculosPadrao.includes(x)).map(x => <option key={x} value={x}>{x}</option>)}
                      <option value="__outro">Outro (digitar)</option>
                    </select>
                    {!VINCULOS_CONVENIA.includes(novoVinculo) && novoVinculo !== '' && (
                      <input className="form-control form-control-sm" style={{ width: 180 }} placeholder="nome como na Convenia"
                        value={novoVinculo.trimStart()} onChange={(e) => setNovoVinculo(e.target.value || ' ')} aria-label="Nome do vínculo" />
                    )}
                    <button type="button" className="btn btn-sm btn-primary" disabled={!novoVinculo.trim()}
                      onClick={() => {
                        const nome = novoVinculo.trim();
                        if (nome && !vinculosPadrao.includes(nome)) setVinculosExtra((l) => [...l, nome]);
                        setNovoVinculo(null);
                      }}>Incluir</button>
                    <button type="button" className="btn btn-sm btn-light border" onClick={() => setNovoVinculo(null)} title="Cancelar">
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
                <div className="form-check form-switch ms-auto"
                  title="Regra da empresa: a maioria paga o dia de férias integral; a Gauss desconta os dias de férias do crédito">
                  <input disabled={travado} className="form-check-input" type="checkbox" id="descontaFerias"
                    checked={Boolean(dados.desconta_ferias)}
                    onChange={(e) => salvar({ tipo: 'config', desconta_ferias: e.target.checked },
                      e.target.checked ? 'Férias passam a descontar do crédito' : 'Férias pagas integralmente')} />
                  <label className="form-check-label small fw-semibold" htmlFor="descontaFerias">
                    Descontar férias do crédito
                  </label>
                </div>
              </div>
            </div>

            {/* filtros */}
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <input type="text" className="form-control form-control-sm"
                  placeholder="Nome, CPF, departamento, cargo..."
                  value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
              <div className="col-md-3">
                <select className="form-select form-select-sm" value={filtroVinculo}
                  onChange={(e) => setFiltroVinculo(e.target.value)}>
                  <option value="">Todos os vínculos</option>
                  {(dados.vinculos || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              {(temFcol || busca || filtroVinculo) && (
                <div className="col-md-auto">
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setFcol(FCOL_VAZIO); setBusca(''); setFiltroVinculo(''); }}>
                    <i className="fas fa-filter-circle-xmark me-1"></i>Limpar filtros
                  </button>
                </div>
              )}
            </div>

            {/* a planilha */}
            <div className="table-responsive bg-white rounded shadow-sm">
              <table className="table table-striped table-hover align-middle mb-0">
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th className="text-center" style={{ width: '44px' }}
                      title="Pago — marca o crédito deste colaborador no mês (fica no histórico)">
                      <i className="fas fa-circle-check text-muted"></i>
                    </th>
                    <th style={{ whiteSpace: 'nowrap' }}>CPF</th>
                    <th><i className="fas fa-user me-1"></i>Colaborador</th>
                    <th>Vínculo</th>
                    {(dados.filiais || []).length > 0 && (
                      <th title="Filial do colaborador (a mesma da folha) — define qual custo por filial vale para ele">Filial</th>
                    )}
                    <th style={{ whiteSpace: 'nowrap' }}>Admissão</th>
                    <th>Departamento</th>
                    <th className="text-center" title="Valor do benefício por dia útil — edite e saia do campo para salvar">
                      Custo dia
                    </th>
                    <th className="text-center" title="Valor FIXO mensal (Rubeus e afins): preenchido, manda no lugar do custo/dia — o crédito é esse valor rateado pelos dias pagos">
                      Valor fixo
                    </th>
                    <th className="text-center" title="Dias úteis a pagar / dias úteis do mês (desconta férias, afastamento, admissão e desligamento)">
                      Dias
                    </th>
                    <th className="text-end">Desc.</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Crédito</th>
                    {extraCfg && (
                      <th className="text-center" style={{ whiteSpace: 'nowrap' }}
                        title={`${extraCfg.nome}: ${brl(extraCfg.valor_padrao)} fixo (sem proporção de dias) para quem está marcado`}>
                        {extraCfg.nome}
                      </th>
                    )}
                    <th style={{ minWidth: '160px' }}>Obs</th>
                    <th className="text-center" style={{ width: 46 }}
                      title="Tirar da lista quem não recebe este benefício">
                      <i className="fas fa-user-minus text-muted"></i>
                    </th>
                  </tr>
                  <tr className="beneficio-filtros">
                    <th className="p-1">
                      <select className="form-select form-select-sm px-1" style={{ minWidth: '64px' }} value={fcol.pago}
                        onChange={(e) => mudarFcol('pago', e.target.value)} title="Filtrar por pago">
                        <option value="">Todos</option><option value="sim">Pago</option><option value="nao">Não pago</option>
                      </select>
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm" placeholder="Filtrar" value={fcol.cpf}
                        onChange={(e) => mudarFcol('cpf', e.target.value)} />
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm" placeholder="Filtrar" value={fcol.nome}
                        onChange={(e) => mudarFcol('nome', e.target.value)} />
                    </th>
                    <th className="p-1">
                      <select className="form-select form-select-sm" value={filtroVinculo}
                        onChange={(e) => setFiltroVinculo(e.target.value)}>
                        <option value="">Todos</option>
                        {(dados.vinculos || []).map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </th>
                    {(dados.filiais || []).length > 0 && (
                      <th className="p-1">
                        <select className="form-select form-select-sm" value={fcol.filial}
                          onChange={(e) => mudarFcol('filial', e.target.value)}>
                          <option value="">Todas</option>
                          <option value="__sem">— sem filial —</option>
                          {dados.filiais.map(fl => <option key={fl.id} value={String(fl.id)}>{fl.nome}</option>)}
                        </select>
                      </th>
                    )}
                    <th className="p-1">
                      <input className="form-control form-control-sm" placeholder="mm/aaaa" value={fcol.admissao}
                        onChange={(e) => mudarFcol('admissao', e.target.value)} style={{ minWidth: '80px' }} />
                    </th>
                    <th className="p-1">
                      <select className="form-select form-select-sm" value={fcol.depto}
                        onChange={(e) => mudarFcol('depto', e.target.value)}>
                        <option value="">Todos</option>
                        {deptosDaLista.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm text-end" placeholder="valor" value={fcol.v_custo_dia}
                        onChange={(e) => mudarFcol('v_custo_dia', e.target.value)} style={{ minWidth: '70px' }}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm text-end" placeholder="valor" value={fcol.v_custo_mes}
                        onChange={(e) => mudarFcol('v_custo_mes', e.target.value)} style={{ minWidth: '70px' }}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm text-end" placeholder="dias" value={fcol.v_dias}
                        onChange={(e) => mudarFcol('v_dias', e.target.value)} style={{ minWidth: '70px' }}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm text-end" placeholder="valor" value={fcol.v_desconto}
                        onChange={(e) => mudarFcol('v_desconto', e.target.value)} style={{ minWidth: '70px' }}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    <th className="p-1">
                      <input className="form-control form-control-sm text-end" placeholder="valor" value={fcol.v_total}
                        onChange={(e) => mudarFcol('v_total', e.target.value)} style={{ minWidth: '70px' }}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    <th className="p-1">
                      <select className="form-select form-select-sm px-1" value={fcol.credito}
                        onChange={(e) => mudarFcol('credito', e.target.value)}>
                        <option value="">Todos</option><option value="com">Com crédito</option><option value="sem">Sem crédito</option>
                      </select>
                      <input className="form-control form-control-sm text-end mt-1" placeholder="valor" value={fcol.v_credito}
                        onChange={(e) => mudarFcol('v_credito', e.target.value)}
                        title="Valor: 226,10 (igual), >200, <=50 ou 100-300 (intervalo)" />
                    </th>
                    {extraCfg && <th></th>}
                    <th className="p-1">
                      <input className="form-control form-control-sm" placeholder="Filtrar" value={fcol.obs}
                        onChange={(e) => mudarFcol('obs', e.target.value)} />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradores.map((c) => (
                    <tr key={c.convenia_id} style={{
                      opacity: c.desligado ? .65 : 1,
                      background: c.pagamento ? '#f2fbf6' : undefined }}>
                      <td className="text-center">
                        <input type="checkbox" className="form-check-input"
                          style={{ cursor: 'pointer' }}
                          checked={Boolean(c.pagamento)}
                          disabled={travado || (!c.pagamento && !c.credito)}
                          onChange={(e) => marcarPago([c], e.target.checked)}
                          title={c.pagamento
                            ? `Pago ${brl(c.pagamento.valor)} em ${c.pagamento.pago_em}`
                              + (c.pagamento.pago_por ? ` por ${c.pagamento.pago_por}` : '')
                            : (c.credito ? 'Marcar como pago' : 'Sem crédito para pagar')} />
                      </td>
                      <td className="text-muted" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: '.85rem' }}>
                        {c.cpf || '—'}
                      </td>
                      <td>
                        <span className="fw-semibold">{c.nome}</span>
                        {(c.alertas || []).map((a, i) => {
                          const f = COR_ALERTA[a.tipo] || COR_ALERTA.experiencia;
                          return (
                            <div key={i} className="small" style={{ color: f.cor }}>
                              <i className={`fas ${f.icone} me-1`}></i>{a.texto}
                            </div>
                          );
                        })}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{c.vinculo}</td>
                      {(dados.filiais || []).length > 0 && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <select disabled={travado} className="form-select form-select-sm"
                            style={{ minWidth: '150px', background: c.filial_id ? '#e3f2fd' : undefined }}
                            value={c.filial_id || ''}
                            onChange={(e) => salvarFilialColab(c, e.target.value)}
                            title={c.filial_id ? `Filial ${c.filial} — o mesmo cadastro da folha` : 'Sem filial: usa o padrão do vínculo'}>
                            <option value="">— sem filial —</option>
                            {dados.filiais.map(fl => <option key={fl.id} value={fl.id}>{fl.nome}</option>)}
                          </select>
                        </td>
                      )}
                      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{c.admissao || '—'}</td>
                      <td className="small">{c.departamento || '—'}</td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                        <div className="input-group input-group-sm" style={{ width: '120px', margin: '0 auto' }}>
                          <span className="input-group-text px-1">R$</span>
                          <input disabled={travado} type="number" step="0.01" min="0"
                            className={`form-control text-end ${c.custo_individual ? 'fw-bold' : ''}`}
                            style={c.custo_individual ? { background: '#fff8e6' } : undefined}
                            key={`${c.convenia_id}-${c.custo_dia ?? ''}-${c.custo_individual}`}
                            defaultValue={c.custo_individual ? c.custo_dia : ''}
                            placeholder={c.custo_dia != null ? String(c.custo_dia) : '—'}
                            title={c.custo_individual
                              ? 'Valor individual — apague e saia do campo para voltar ao padrão do vínculo'
                              : (c.custo_dia != null
                                ? `${c.origem_custo === 'filial' ? `Padrão da filial (${c.filial})` : `Padrão do vínculo (${c.vinculo})`} — digite para dar um valor só a esta pessoa`
                                : 'Sem custo definido — defina o padrão do vínculo acima ou digite aqui')}
                            onBlur={(e) => salvarCusto(c, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                        </div>
                      </td>
                      <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                        <div className="input-group input-group-sm" style={{ width: '130px', margin: '0 auto' }}>
                          <span className="input-group-text px-1">R$</span>
                          <input disabled={travado} type="number" step="0.01" min="0"
                            className={`form-control text-end ${c.custo_mes != null ? 'fw-bold' : ''}`}
                            style={c.custo_mes != null ? { background: '#e6f7ef' } : undefined}
                            key={`m-${c.convenia_id}-${c.custo_mes ?? ''}`}
                            defaultValue={c.custo_mes != null ? c.custo_mes : ''}
                            placeholder="—"
                            title={c.custo_mes != null
                              ? 'Valor fixo mensal — manda no lugar do custo/dia. Apague e saia do campo para voltar ao custo/dia.'
                              : 'Valor fixo mensal deste colaborador (opcional). Preenchido, o crédito é este valor rateado pelos dias pagos.'}
                            onBlur={(e) => salvarFixo(c, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                        </div>
                      </td>
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <div className="d-inline-flex align-items-center gap-1">
                          <input disabled={travado} type="text" className="form-control form-control-sm text-center"
                            style={{ width: 52,
                              borderColor: c.dias_manual ? '#0a6ea1'
                                : (c.dias_pagar < c.dias_uteis ? '#f0a868' : undefined) }}
                            defaultValue={c.dias_pagar}
                            key={`d-${c.convenia_id}-${c.dias_pagar}-${c.dias_manual}`}
                            onBlur={(e) => {
                              if (String(e.target.value).trim() === String(c.dias_pagar)) return;
                              salvarDias(c, e.target.value);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            title={c.dias_manual
                              ? 'Dias ajustados à mão nesta competência — apague e saia do campo para voltar ao calculado'
                              : 'Dias calculados (férias/afastamento e proporcional já descontados). Digite para ajustar só esta pessoa neste mês.'} />
                          <span className="text-muted">/{c.dias_uteis}</span>
                        </div>
                        {c.dias_manual && (
                          <div className="small" style={{ color: '#0a6ea1' }}>ajustado</div>
                        )}
                      </td>
                      <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', color: c.desconto ? '#a11d2b' : '#adb5bd' }}>
                        {c.desconto ? brl(c.desconto) : '—'}
                      </td>
                      <td className="text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {c.custo_dia != null ? brl(c.total) : '—'}
                      </td>
                      <td className="text-end fw-bold" style={{ fontVariantNumeric: 'tabular-nums', color: '#116b47' }}>
                        {c.custo_dia != null ? brl(c.credito) : '—'}
                      </td>
                      {extraCfg && (
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <input type="checkbox" className="form-check-input me-1" style={{ cursor: 'pointer' }}
                            disabled={travado || mexendoExtra === c.convenia_id}
                            checked={Boolean(c.extra_marcado)}
                            onChange={(e) => salvarExtra(c, e.target.checked)}
                            title={c.extra_marcado ? `Tirar ${extraCfg.nome}` : `Dar ${extraCfg.nome} (${brl(extraCfg.valor_padrao)})`} />
                          {c.extra_marcado && (
                            <input disabled={travado} type="number" step="0.01" min="0"
                              className="form-control form-control-sm d-inline-block text-end"
                              style={{ width: 90 }}
                              key={`${c.convenia_id}-extra-${c.extra_valor}`}
                              defaultValue={c.extra_valor ?? ''}
                              onBlur={(e) => {
                                if (Number(e.target.value) !== Number(c.extra_valor)) salvarExtra(c, true, e.target.value);
                              }}
                              title={c.desligado ? 'Desligado: não recebe neste mês' : 'Valor fixo deste colaborador'} />
                          )}
                        </td>
                      )}
                      <td>
                        <input disabled={travado} type="text" className="form-control form-control-sm"
                          key={`${c.convenia_id}-obs-${c.obs || ''}`}
                          defaultValue={c.obs || ''} placeholder="—"
                          onBlur={(e) => salvarObs(c, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                      </td>
                      <td className="text-center">
                        <button className="btn btn-sm btn-light border p-1 px-2"
                          disabled={travado || mexendoParticipante === c.convenia_id}
                          onClick={() => setTirando(c)}
                          title={`${c.nome} não recebe ${rotulo.curto} — tirar da lista`}>
                          {mexendoParticipante === c.convenia_id
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <i className="fas fa-user-minus text-danger"
                                style={{ fontSize: '.75rem' }}></i>}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {colaboradores.length === 0 && (
                    <tr><td colSpan={14 + ((dados.filiais || []).length > 0 ? 1 : 0) + (extraCfg ? 1 : 0)} className="text-center text-muted py-4">
                      <i className="fas fa-inbox me-2"></i>Nenhum colaborador neste filtro.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <DiferencaMesAnterior url={`${API}/api/bpo-beneficios/${clienteId}/${q}`} mes={mes}
              atual={dados} extrair={pessoasDoMes} rotulo={rotulo.curto} />

            {(dados.candidatos || []).length > 0 && (
              <div className="mt-3">
                <button disabled={travado} className="btn btn-sm btn-outline-primary"
                  onClick={() => { setIncluindoAberto(true); setBuscaCandidato(''); }}
                  title={`Incluir no ${rotulo.curto} alguém que não está na lista`}>
                  <i className="fas fa-user-plus me-2"></i>
                  Incluir colaborador
                  <span className="badge bg-light text-muted border ms-2">
                    {dados.candidatos.length} fora da lista
                  </span>
                </button>
              </div>
            )}

            {(dados.excluidos || []).length > 0 && (
              <div className="bg-white rounded shadow-sm p-3 mt-3"
                style={{ borderLeft: '4px solid #adb5bd' }}>
                <button className="btn btn-link p-0 fw-bold text-decoration-none"
                  style={{ color: '#00205b' }}
                  onClick={() => setVerExcluidos(v => !v)}>
                  <i className={`fas fa-chevron-${verExcluidos ? 'down' : 'right'} me-2`}></i>
                  Não recebem {rotulo.curto}
                  <span className="badge bg-secondary ms-2">{dados.excluidos.length}</span>
                </button>
                {verExcluidos && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm align-middle mb-0">
                      <tbody>
                        {dados.excluidos.map(x => (
                          <tr key={x.convenia_id}>
                            <td className="fw-semibold">{x.nome}</td>
                            <td className="small text-muted">{x.cargo || '—'}</td>
                            <td className="small text-muted">{x.vinculo || '—'}</td>
                            <td className="small text-muted">{x.motivo || ''}</td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-sm btn-outline-success"
                                disabled={mexendoParticipante === x.convenia_id}
                                onClick={() => definirParticipa(x, true)}
                                title={`Incluir ${x.nome} de volta no ${rotulo.curto}`}>
                                {mexendoParticipante === x.convenia_id
                                  ? <span className="spinner-border spinner-border-sm"></span>
                                  : <><i className="fas fa-user-plus me-1"></i>Incluir</>}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <p className="text-muted small mt-3 mb-0">
              <i className="fas fa-circle-info me-1"></i>
              O benefício é comprado <b>adiantado</b> — o mês-base default é o mês seguinte.{' '}
              <b>Dias</b>: úteis a pagar / úteis do mês — admissão, desligamento e afastamentos
              descontam automaticamente; <b>férias {dados.desconta_ferias
                ? 'descontam nesta empresa' : 'são pagas integralmente nesta empresa'}</b>{' '}
              (chave acima). <b>Total</b> = custo × úteis do mês; <b>Crédito</b> = custo × dias
              a pagar. Feriado não desconta automaticamente — ajuste na observação. Edições
              salvam ao sair do campo.
            </p>
          </>
        )}

        {/* ─── MODAL: confirmar pagamento em lote ─── */}
        {confirmarPgto && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-circle-check me-2 text-success"></i>
                      Marcar todos como pagos
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {dados?.cliente} · {rotRef} {mesExtenso(dados?.mes_uso || mesDepois(mes))} · {emAtraso ? `recebe em ${mesExtenso(mesRecebe)}` : `pago em ${mesExtenso(mesPago)}`}
                    </small>
                  </div>
                  <button type="button" className="btn-close"
                    onClick={() => setConfirmarPgto(false)} disabled={salvandoPgto}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    Confirmar o pagamento de <b>{faltamPagar.length} colaborador(es)</b>,
                    totalizando{' '}
                    <b style={{ color: '#116b47' }}>
                      {brl(faltamPagar.reduce((s, c) => s + (c.credito || 0), 0))}
                    </b>?
                  </p>
                  {(busca || filtroVinculo || temFcol) && (
                    <div className="alert alert-warning py-2 small mb-2">
                      <i className="fas fa-filter me-1"></i>
                      Há filtro ativo — só os colaboradores <b>visíveis na tabela</b> serão marcados.
                    </div>
                  )}
                  <small className="text-muted">
                    O valor e os dias são gravados no histórico deste mês (não mudam se o
                    cálculo for refeito depois) — é o que responde “quanto o fulano recebeu
                    de VA em {mesExtenso(mes)}”. Dá para desmarcar um a um pelo checkbox.
                  </small>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary"
                    onClick={() => setConfirmarPgto(false)} disabled={salvandoPgto}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  <button type="button" className="btn btn-success"
                    onClick={() => { marcarPago(faltamPagar, true); setConfirmarPgto(false); }}
                    disabled={salvandoPgto}>
                    <i className={`fas ${salvandoPgto ? 'fa-spinner fa-spin' : 'fa-circle-check'} me-2`}></i>
                    Confirmar pagamento
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── MODAL: CALENDÁRIO DE FERIADOS ─── */}
      {feriadosAberto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-umbrella-beach me-2" style={{ color: '#8a6d00' }}></i>
                    Calendário de feriados · {anoDoMes}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {dados?.cliente} · feriado em dia útil tira um dia do crédito
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setFeriadosAberto(false)}></button>
              </div>

              <div className="modal-body">
                {/* cadastrar */}
                <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                  <div className="small fw-bold text-muted mb-2">
                    <i className="fas fa-plus me-2"></i>Adicionar feriado
                  </div>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label small mb-1">Data</label>
                      <input type="date" className="form-control form-control-sm"
                        value={feriadoNovo?.data || ''}
                        onChange={(e) => setFeriadoNovo(f => ({ ...f, data: e.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1">Nome</label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Ex.: Aniversário da cidade"
                        value={feriadoNovo?.nome || ''}
                        onChange={(e) => setFeriadoNovo(f => ({ ...f, nome: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && feriadoNovo?.nome) salvarFeriado(); }} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-1">Tipo</label>
                      <select className="form-select form-select-sm" value={feriadoNovo?.tipo || 'municipal'}
                        onChange={(e) => setFeriadoNovo(f => ({ ...f, tipo: e.target.value }))}>
                        <option value="municipal">Municipal</option>
                        <option value="estadual">Estadual</option>
                        <option value="nacional">Nacional</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <button className="btn btn-sm btn-success w-100"
                        disabled={!feriadoNovo?.nome?.trim() || !feriadoNovo?.data}
                        onClick={salvarFeriado}>
                        <i className="fas fa-check me-2"></i>Adicionar
                      </button>
                    </div>
                  </div>
                  <div className="form-check mt-2">
                    <input className="form-check-input" type="checkbox" id="feriadoGeral"
                      checked={Boolean(feriadoNovo?.geral)}
                      onChange={(e) => setFeriadoNovo(f => ({ ...f, geral: e.target.checked }))} />
                    <label className="form-check-label small" htmlFor="feriadoGeral">
                      Vale para <b>todas as empresas</b> (deixe desmarcado para valer só
                      na {dados?.cliente})
                    </label>
                  </div>
                </div>

                {/* lista do ano */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="small fw-bold text-muted">
                    <i className="fas fa-list me-2"></i>
                    Feriados de {anoDoMes} ({(feriadosAno?.feriados || []).length})
                  </div>
                  <button className="btn btn-sm btn-outline-primary" onClick={importarNacionais}
                    title="Traz os feriados nacionais do ano (BrasilAPI)">
                    <i className="fas fa-cloud-arrow-down me-2"></i>Importar nacionais
                  </button>
                </div>

                {carregandoFeriados ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary"></div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead style={{ background: '#f8f9fa' }}>
                        <tr>
                          <th>Data</th>
                          <th>Feriado</th>
                          <th>Tipo</th>
                          <th>Vale para</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(feriadosAno?.feriados || []).map(fer => {
                          const noMes = fer.data_iso.slice(0, 7) === mes;
                          // linha em edição: os campos tomam o lugar do texto
                          if (feriadoEdit?.id === fer.id) {
                            return (
                              <tr key={fer.id} style={{ background: '#eef6ff' }}>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <input type="date" className="form-control form-control-sm"
                                    style={{ maxWidth: 150 }}
                                    value={feriadoEdit.data}
                                    onChange={(e) => setFeriadoEdit(f => ({ ...f, data: e.target.value }))} />
                                </td>
                                <td>
                                  <input type="text" className="form-control form-control-sm"
                                    value={feriadoEdit.nome}
                                    onChange={(e) => setFeriadoEdit(f => ({ ...f, nome: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') salvarEdicaoFeriado(); }}
                                    autoFocus />
                                </td>
                                <td>
                                  <select className="form-select form-select-sm"
                                    value={feriadoEdit.tipo}
                                    onChange={(e) => setFeriadoEdit(f => ({ ...f, tipo: e.target.value }))}>
                                    <option value="municipal">municipal</option>
                                    <option value="estadual">estadual</option>
                                    <option value="nacional">nacional</option>
                                  </select>
                                </td>
                                <td>
                                  <div className="form-check small m-0">
                                    <input className="form-check-input" type="checkbox"
                                      id={`fer-geral-${fer.id}`}
                                      checked={Boolean(feriadoEdit.geral)}
                                      onChange={(e) => setFeriadoEdit(f => ({ ...f, geral: e.target.checked }))} />
                                    <label className="form-check-label" htmlFor={`fer-geral-${fer.id}`}>
                                      todas as empresas
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                  <button className="btn btn-sm btn-success p-1 px-2 me-1"
                                    disabled={salvandoFeriado || !feriadoEdit.nome.trim() || !feriadoEdit.data}
                                    onClick={salvarEdicaoFeriado}
                                    title="Salvar as alterações">
                                    {salvandoFeriado
                                      ? <span className="spinner-border spinner-border-sm"></span>
                                      : <i className="fas fa-check" style={{ fontSize: '.75rem' }}></i>}
                                  </button>
                                  <button className="btn btn-sm btn-light border p-1 px-2"
                                    onClick={() => setFeriadoEdit(null)}
                                    title="Cancelar a edição">
                                    <i className="fas fa-times text-muted" style={{ fontSize: '.75rem' }}></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={fer.id}
                              style={{ background: noMes ? '#fff8e1' : undefined }}>
                              <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {fer.data}
                                {noMes && (
                                  <span className="badge ms-2"
                                    style={{ background: '#fff5da', color: '#8a6d00',
                                      border: '1px solid #f0dfa8' }}>
                                    neste mês
                                  </span>
                                )}
                              </td>
                              <td>{fer.nome}</td>
                              <td className="text-muted small">
                                {fer.tipo}
                                {fer.cidade && <> · {fer.cidade}</>}
                                {fer.uf && !fer.cidade && <> · {fer.uf}</>}
                              </td>
                              <td className="small text-muted">{fer.cliente}</td>
                              <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                <button className="btn btn-sm btn-light border p-1 px-2 me-1"
                                  onClick={() => setFeriadoEdit({
                                    id: fer.id, data: fer.data_iso, nome: fer.nome,
                                    tipo: fer.tipo, geral: !fer.cliente_id,
                                  })}
                                  title="Editar este feriado">
                                  <i className="fas fa-pen" style={{ fontSize: '.75rem', color: '#0a6ea1' }}></i>
                                </button>
                                <button className="btn btn-sm btn-light border p-1 px-2"
                                  onClick={() => removerFeriado(fer)}
                                  title="Remover do calendário">
                                  <i className="fas fa-trash text-danger"
                                    style={{ fontSize: '.75rem' }}></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {!(feriadosAno?.feriados || []).length && (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-4">
                              Nenhum feriado cadastrado em {anoDoMes}. Use
                              <b> Importar nacionais</b> para começar.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="alert alert-light border small mt-3 mb-0">
                  <i className="fas fa-circle-info me-2 text-primary"></i>
                  A Convenia não expõe o calendário dela pela API, então os feriados
                  municipais e estaduais são cadastrados aqui. Feriado em fim de semana
                  fica registrado, mas não altera o crédito.
                </div>
              </div>

              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">
                  Mudanças aqui recalculam o crédito do mês na hora.
                </span>
                <button className="btn btn-secondary" onClick={() => setFeriadosAberto(false)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: BOLETO DA RECARGA E ENVIO AO FINANCEIRO ─── */}
      {boletoAberto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-invoice-dollar me-2"></i>
                    Boleto do {tipo === 'home_office' ? 'auxílio home office' : 'vale-alimentação'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {dados?.cliente} · {rotRef} {mesExtenso(dados?.mes_uso || mesDepois(mes))} · {emAtraso ? `recebe em ${mesExtenso(mesRecebe)}` : `pago em ${mesExtenso(mesPago)}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => { setBoletoAberto(false); setBoletoPrevia(null); }}
                  disabled={enviandoBoleto}></button>
              </div>

              <div className="modal-body">
                {/* 1. anexo */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-2">
                  <i className="fas fa-paperclip me-2"></i>Boleto em anexo
                </h6>
                <input type="file" accept="application/pdf,.pdf" ref={boletoInput}
                  onChange={anexarBoleto} style={{ display: 'none' }} />
                <button className="btn btn-sm btn-outline-primary"
                  disabled={subindoBoleto}
                  onClick={() => boletoInput.current?.click()}>
                  {subindoBoleto
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                    : <><i className="fas fa-file-arrow-up me-2"></i>Anexar boleto (PDF)</>}
                </button>

                {(boletoDados?.boletos || []).map(b => (
                  <div key={b.id} className="d-flex align-items-center gap-2 mt-2 small">
                    <input type="checkbox" className="form-check-input"
                      checked={boletoSel.includes(b.id)}
                      onChange={(e) => { setBoletoSel(sel => (e.target.checked
                        ? [...sel, b.id] : sel.filter(x => x !== b.id))); setBoletoPrevia(null); }}
                      title="Incluir este boleto no e-mail" />
                    <i className="fas fa-file-pdf" style={{ color: '#a11d2b' }}></i>
                    <a href={`${API}/api/bpo-beneficios/${clienteId}/boleto/${b.id}/${q}`}
                      target="_blank" rel="noreferrer"
                      style={{ textDecoration: 'none' }}>{b.nome}</a>
                    <span className="text-muted" style={{ fontSize: '.72rem' }}>
                      {b.enviado_em}{b.enviado_por ? ` · ${b.enviado_por}` : ''}
                    </span>
                    <button className="btn btn-sm btn-light border p-1"
                      onClick={() => removerBoleto(b)} title="Remover">
                      <i className="fas fa-trash text-danger" style={{ fontSize: '.7rem' }}></i>
                    </button>
                  </div>
                ))}
                {!(boletoDados?.boletos || []).length && (
                  <div className="small text-muted mt-2">
                    Nenhum boleto anexado nesta competência.
                  </div>
                )}

                {/* 2. destinatários */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                  <i className="fas fa-address-book me-2"></i>Financeiro do cliente
                </h6>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Para</label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="financeiro@cliente.com.br"
                      value={boletoForm.emails}
                      onChange={(e) => { setBoletoForm(f => ({ ...f, emails: e.target.value })); setBoletoPrevia(null); }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Cópia (opcional)</label>
                    <input type="text" className="form-control form-control-sm"
                      value={boletoForm.copia}
                      onChange={(e) => { setBoletoForm(f => ({ ...f, copia: e.target.value })); setBoletoPrevia(null); }} />
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 mt-2">
                  <div>
                    <label className="form-label small fw-semibold">Vencimento do boleto</label>
                    <input type="text" className="form-control form-control-sm"
                      style={{ width: '150px' }} placeholder="05/09/2026"
                      value={boletoForm.vencimento}
                      onChange={(e) => { setBoletoForm(f => ({ ...f, vencimento: e.target.value })); setBoletoPrevia(null); }} />
                  </div>
                  <div>
                    <label className="form-label small fw-semibold">Taxa do boleto</label>
                    <input type="text" className="form-control form-control-sm"
                      style={{ width: '120px' }} placeholder="0,00"
                      value={boletoForm.taxa_boleto}
                      onChange={(e) => { setBoletoForm(f => ({ ...f, taxa_boleto: e.target.value })); setBoletoPrevia(null); }}
                      title="Rateada igualmente entre os colaboradores SÓ no relatório do e-mail — não entra no arquivo da plataforma nem no crédito." />
                  </div>
                  {tipo === 'va' && (
                    <div className="form-check mb-1">
                      <input className="form-check-input" type="checkbox" id="vaAnexarOmie" checked={anexarOmie}
                        onChange={(e) => { setBoletoForm(f => ({ ...f, anexar_omie: e.target.checked })); setBoletoPrevia(null); }} />
                      <label className="form-check-label small" htmlFor="vaAnexarOmie"
                        title="Anexa a planilha de Contas a Pagar da Omie (os pagos, uma conta por colaborador, com o vencimento deste boleto)">
                        Anexar planilha Omie
                      </label>
                    </div>
                  )}
                  <div>
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }}
                      value={boletoOperador}
                      onChange={(e) => { setBoletoOperador(e.target.value); setBoletoPrevia(null); }}
                      title="O e-mail sai da conta deste operador">
                      <option value="">— escolha o operador —</option>
                      {(boletoDados?.operadores || []).map(o => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>
                          {o.nome}{o.pronto ? '' : ' (sem senha de app)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-sm btn-outline-secondary" onClick={salvarFinanceiro}
                    title="Guardar estes e-mails para os próximos meses">
                    <i className="fas fa-save me-2"></i>Salvar e-mails
                  </button>
                  <button className="btn btn-sm btn-primary ms-auto" onClick={previaBoleto}>
                    <i className="fas fa-eye me-2"></i>Ver o e-mail
                  </button>
                </div>
                <div className="form-text">
                  O resumo do e-mail usa os mesmos {linhasDoArquivo.length} colaborador(es) do
                  arquivo da plataforma{marcados.length ? ' (só os marcados)' : ''}.
                </div>

                {/* 3. prévia */}
                {boletoPrevia && (
                  <>
                    <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                      <i className="fas fa-envelope-open-text me-2"></i>Prévia
                    </h6>
                    <div className="small mb-2">
                      <div><b>Assunto:</b> {boletoPrevia.assunto}</div>
                      <div><b>De:</b> {boletoPrevia.remetente || '—'}</div>
                      <div><b>Para:</b> {boletoPrevia.destinatarios || '—'}</div>
                      {boletoPrevia.copia && <div><b>Cópia:</b> {boletoPrevia.copia}</div>}
                      <div>
                        <b>Anexos:</b>{' '}
                        {boletoPrevia.anexos?.length
                          ? boletoPrevia.anexos.map(a => a.nome).join(', ')
                          : <span className="text-danger">nenhum — anexe o boleto</span>}
                        {boletoPrevia.relatorio && (
                          <span className="ms-1 text-success" title="Planilha com a relação por colaborador, gerada na hora do envio">
                            + <i className="fas fa-file-excel me-1"></i>{boletoPrevia.relatorio}
                          </span>
                        )}
                        {boletoPrevia.omie && (boletoPrevia.omie.problema ? (
                          <div className="text-danger">
                            <i className="fas fa-triangle-exclamation me-1"></i>Planilha Omie: {boletoPrevia.omie.problema}
                          </div>
                        ) : (
                          <span className="ms-1 text-success" title="Contas a Pagar da Omie: uma conta por colaborador pago">
                            + <i className="fas fa-file-excel me-1"></i>{boletoPrevia.omie.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="border rounded" style={{ maxHeight: '340px', overflow: 'auto' }}>
                      {/* sandbox="" é obrigatório: srcdoc herda a origem do app,
                          então script no HTML da prévia alcançaria o token no
                          localStorage. O logo em data: URI segue funcionando. */}
                      <iframe title="Prévia do e-mail do boleto"
                        srcDoc={boletoPrevia.corpo_previa || boletoPrevia.corpo_html}
                        sandbox=""
                        style={{ width: '100%', height: '340px', border: 0 }} />
                    </div>
                  </>
                )}

                {/* 4. histórico */}
                {(boletoDados?.historico || []).length > 0 && (
                  <>
                    <h6 className="fw-bold text-muted border-bottom pb-1 mb-2 mt-4">
                      <i className="fas fa-clock-rotate-left me-2"></i>
                      Já enviados nesta competência
                    </h6>
                    {boletoDados.historico.map((h, i) => (
                      <div key={i} className="small text-muted border-bottom py-1">
                        <b style={{ color: '#212529' }}>{h.enviado_em}</b> · {h.destinatarios}
                        {h.colaboradores != null && ` · ${h.colaboradores} colaborador(es)`}
                        {h.valor_total != null && ` · ${brl(h.valor_total)}`}
                        {h.anexos && ` · ${h.anexos}`}
                        {h.enviado_por && ` · por ${h.enviado_por}`}
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">
                  O e-mail sai com o boleto em anexo e o resumo por colaborador.
                </span>
                <button className="btn btn-secondary" disabled={enviandoBoleto}
                  onClick={() => { setBoletoAberto(false); setBoletoPrevia(null); }}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                {/* prévia obrigatória (regra de 09/09/2026), sem botão apagado: o 1º
                    clique monta o e-mail aqui embaixo, o 2º confirma o envio */}
                <button className="btn btn-success" onClick={boletoPrevia ? enviarBoleto : previaBoleto}
                  disabled={enviandoBoleto || !boletoSel.length
                    || !boletoForm.emails.trim() || !boletoOperador}
                  title={!boletoPrevia
                    ? 'Mostra o e-mail montado (assunto, destinatários, anexos) — o envio é confirmado em seguida'
                    : 'Envia exatamente o e-mail mostrado na prévia'}>
                  <i className={`fas ${enviandoBoleto ? 'fa-spinner fa-spin' : (boletoPrevia ? 'fa-paper-plane' : 'fa-eye')} me-2`}></i>
                  {enviandoBoleto ? 'Enviando...' : (boletoPrevia ? 'Confirmar envio ao financeiro' : 'Enviar ao financeiro')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: INCLUIR QUEM ESTÁ FORA DA LISTA ─── */}
      {incluindoAberto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000,
            overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-plus me-2"></i>
                    Incluir no {rotulo.curto}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {dados?.cliente} · {rotRef} {mesExtenso(dados?.mes_uso || mesDepois(mes))} · {emAtraso ? `recebe em ${mesExtenso(mesRecebe)}` : `pago em ${mesExtenso(mesPago)}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setIncluindoAberto(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-light border small">
                  <i className="fas fa-circle-info me-2 text-primary"></i>
                  Quem aparece aqui está no cadastro da Convenia mas ficou fora do mês —
                  desligado antes da competência ou admitido depois dela. Ao incluir, entra
                  na lista e no total, com os dias calculados normalmente.
                </div>
                <input type="text" className="form-control form-control-sm mb-2"
                  placeholder="Buscar por nome, CPF ou cargo..."
                  value={buscaCandidato}
                  onChange={(e) => setBuscaCandidato(e.target.value)}
                  autoFocus />
                <div className="table-responsive" style={{ maxHeight: '48vh' }}>
                  <table className="table table-sm table-hover align-middle mb-0">
                    <tbody>
                      {(dados.candidatos || [])
                        .filter(c => {
                          const b = buscaCandidato.trim().toLowerCase();
                          if (!b) return true;
                          return [c.nome, c.cpf, c.cargo, c.vinculo]
                            .filter(Boolean).join(' ').toLowerCase().includes(b);
                        })
                        .map(c => (
                          <tr key={c.convenia_id}>
                            <td className="fw-semibold">{c.nome}</td>
                            <td className="small text-muted">{c.cargo || '—'}</td>
                            <td className="small text-muted">{c.vinculo || '—'}</td>
                            <td className="small" style={{ color: '#8a6d00' }}>
                              <i className="fas fa-triangle-exclamation me-1"></i>{c.situacao}
                            </td>
                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-sm btn-outline-success"
                                disabled={travado || mexendoParticipante === c.convenia_id}
                                onClick={() => definirParticipa(c, true, c.situacao, true)}
                                title={`Incluir ${c.nome} no ${rotulo.curto}`}>
                                {mexendoParticipante === c.convenia_id
                                  ? <span className="spinner-border spinner-border-sm"></span>
                                  : <><i className="fas fa-user-plus me-1"></i>Incluir</>}
                              </button>
                            </td>
                          </tr>
                        ))}
                      {(dados.candidatos || []).length === 0 && (
                        <tr><td className="text-center text-muted py-4">
                          Todo o cadastro da Convenia já está na lista.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setIncluindoAberto(false)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: TIRAR DA LISTA DE QUEM RECEBE ─── */}
      {tirando && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-minus me-2"></i>
                    Tirar do {rotulo.curto}
                  </h5>
                  <small style={{ color: '#6c757d' }}>{tirando.nome}</small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => { setTirando(null); setMotivoTirar(''); }}></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  <b>{tirando.nome}</b> sai da lista e do total do pedido. O cadastro na
                  Convenia não muda — e você pode incluir de volta a qualquer momento, no
                  painel <b>“Não recebem {rotulo.curto}”</b> no fim da tela.
                </p>
                <label className="form-label small fw-bold text-muted mb-1">
                  Motivo (opcional, mas ajuda quem olhar depois)
                </label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Ex.: trabalha presencial, não previsto em contrato..."
                  value={motivoTirar}
                  onChange={(e) => setMotivoTirar(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      definirParticipa(tirando, false, motivoTirar);
                      setTirando(null); setMotivoTirar('');
                    }
                  }}
                  autoFocus />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary"
                  onClick={() => { setTirando(null); setMotivoTirar(''); }}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-danger"
                  onClick={() => {
                    definirParticipa(tirando, false, motivoTirar);
                    setTirando(null); setMotivoTirar('');
                  }}>
                  <i className="fas fa-user-minus me-2"></i>Não recebe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {omieVa && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-export me-2"></i>Planilha Omie — vale-alimentação
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {dados?.cliente} · só os colaboradores marcados como pagos · uma conta por colaborador
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setOmieVa(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold">Vencimento do boleto</label>
                    <input type="text" className="form-control form-control-sm" placeholder="30/10/2026"
                      value={omieVa.vencimento} onChange={(e) => mudarOmieVa('vencimento', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold">Conta corrente na Omie</label>
                    <input type="text" className="form-control form-control-sm" value={omieVa.conta}
                      onChange={(e) => mudarOmieVa('conta', e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-semibold">Categoria na Omie</label>
                    <input type="text" className="form-control form-control-sm" value={omieVa.categoria}
                      onChange={(e) => mudarOmieVa('categoria', e.target.value)} />
                  </div>
                  <div className="col-md-3 text-end">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => previaOmieVa(omieVa)} disabled={omieVa.carregando}>
                      <i className={`fas ${omieVa.carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar prévia
                    </button>
                  </div>
                </div>
                {omieVa.previa && (
                  <>
                    <div className="table-responsive mt-3" style={{ maxHeight: 460, overflowY: 'auto' }}>
                      <table className="table table-sm align-middle" style={{ fontSize: '.82rem' }}>
                        <thead className="table-light">
                          <tr><th>Fornecedor (colaborador)</th><th>Projeto</th><th>Departamento</th><th className="text-end">Valor</th></tr>
                        </thead>
                        <tbody>
                          {(omieVa.previa.linhas || []).map((l) => (
                            <tr key={l.convenia_id || l.cpf || l.nome}>
                              <td className="fw-semibold">{l.nome}</td>
                              <td>{l.projeto || <span className="text-warning-emphasis small">sem projeto</span>}</td>
                              <td>
                                {l.pendencias.includes('departamento') ? (
                                  <select className="form-select form-select-sm border-warning" value={omieVa.departamentos[l.depto_convenia] || ''}
                                    onChange={(e) => mudarOmieVa('departamentos', { ...omieVa.departamentos, [l.depto_convenia]: e.target.value })}
                                    title={`Na Convenia: ${l.depto_convenia || 'vazio'}`}>
                                    <option value="">Escolha… ({l.depto_convenia || 'sem depto'})</option>
                                    {(omieVa.previa.departamentos || []).map(d => <option key={d} value={d}>{d}</option>)}
                                  </select>
                                ) : l.departamento}
                              </td>
                              <td className="text-end fw-bold">{brl(l.valor)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="fw-bold"><td colSpan={3} className="text-end">Total ({omieVa.previa.marcados} colaborador(es))</td>
                            <td className="text-end" style={{ color: '#116b47' }}>{brl(omieVa.previa.total)}</td></tr>
                        </tfoot>
                      </table>
                    </div>
                    {(omieVa.previa.avisos || []).map((a, i) => (
                      <div key={i} className="small text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>
                    ))}
                    <div className="form-text">
                      Projeto pelo CNPJ da filial do colaborador (Convenia). Previsão = data do crédito{omieVa.previa.credito_data ? ` (${omieVa.previa.credito_data})` : ''}.
                      Departamento escolhido aqui fica salvo para o colaborador. Depois de mudar algo, clique em "Atualizar prévia".
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">
                  {(() => {
                    const falta = [...new Set((omieVa.previa?.linhas || []).flatMap(l => l.pendencias))];
                    return falta.length
                      ? <span className="text-warning-emphasis"><i className="fas fa-triangle-exclamation me-1"></i>Para baixar, resolva: {falta.map(f => ({ vencimento: 'vencimento do boleto', departamento: 'departamento' }[f] || f)).join(' e ')} e atualize a prévia.</span>
                      : 'Também vai anexada ao e-mail do boleto ("Anexar planilha Omie").';
                  })()}
                </span>
                <button className="btn btn-secondary" onClick={() => setOmieVa(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                <button className="btn btn-success" onClick={baixarOmieVa}
                  disabled={omieVa.carregando || !omieVa.previa || !(omieVa.previa.linhas || []).length
                    || (omieVa.previa.linhas || []).some(l => l.pendencias.length)}>
                  <i className="fas fa-download me-2"></i>Baixar planilha
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhBeneficios;
